// @twasta/page-schema — single source of truth for the Twasta page model.
//
// Consumed by both the designer (frontend/) and the standalone runtime
// client (runtime/client/, in progress). Shape only — no behaviour, no
// runtime deps. See packages/page-schema/README.md.
//
// Page (Smart Page, obj_type "S") model — Notion-style block document
// with embedded transaction objects, page-level input parameters, and a
// rich set of website-style components for building beautiful pages.

export type PageInputType =
  | 'text'
  | 'textarea'            // multi-line free text
  | 'number'
  | 'date'
  | 'datetime'            // date + time
  | 'time'                // time only
  | 'checkbox'
  | 'select'              // dropdown
  | 'spinner'             // number with stepper buttons
  | 'percentage'          // 0-100 number with % suffix
  | 'slider'              // single value, min..max with optional step
  | 'range'               // two values: low..high
  | 'date_range'          // two dates: start..end
  | 'json'                // structured value (object/array). Typically
                          // sourced from an `api` (omit `pickPath` to
                          // capture the full response body). Never
                          // rendered as a form control; consumed by
                          // bindings or {{name.dotted.path}} tokens.

export interface PageInputOption {
  value: string
  label: string
}

// ── Variables ─────────────────────────────────────────────────────────
// `PageInput` is the base shape for **variables** on the page. Sources
// are NOT mutually exclusive — a single variable can have any
// combination of:
//
//   isInput=true   → renders as a form control the user can type into
//   default        → seed/initial value
//   api            → fetched from a REST endpoint, either on page load
//                    (`autoload: true`) or whenever a variable in
//                    `triggerOn` changes
//   expression     → re-evaluated every time a referenced variable
//                    changes; serves as a computed default
//
// Value semantics: last write wins. Whichever source produced the most
// recent value is what the page sees as `{{name}}`. The user can always
// type to override; subsequent automatic refires (API trigger, computed
// re-eval) will then re-overwrite. This matches "default with override"
// when API/computed only fire on specific events, and "live data" when
// they fire continuously.
//
// Independently, `display: true` hides the variable from `input_group`
// form rendering — useful for read-only API/computed values that should
// still feed `{{name}}` or the variable display block.
//
// `kind` is retained as a hint for older saved pages and the AI prompt;
// the editor and runtime both work purely off the presence of source
// fields and ignore `kind` for behaviour decisions.

export type VariableKind = 'input' | 'api' | 'static' | 'computed'

export interface ApiVariableSource {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  url: string                    // supports {{var}} substitution
  // IOFlow-routed API (see CallApiStep.ioflow_api) — POSTs through the
  // surface's /integration/invoke proxy; url/headers ignored when set.
  ioflow_api?: string
  headers?: Record<string, string>
  body?: unknown                 // JSON body; values support {{var}}
  // Dotted path into the response, e.g. 'data.0.email'. Omit it on a
  // `type: 'json'` variable to capture the ENTIRE response body as
  // the variable's value — blocks can then bind to fields inside it
  // via `bindings` or `{{name.path.to.field}}` tokens.
  pickPath?: string
  autoload?: boolean             // fetch on page load (default false)
  triggerOn?: string[]           // refire whenever any of these variables changes
}

/**
 * Populate a page variable from the page CART.
 *
 * The cart is a client-side basket with no server behind it, so until this
 * existed nothing could read it: a visitor could fill a basket and then had
 * to key the order by hand. This maps the basket into the shape an embed's
 * `recordVar` seeds a transaction from — a draft whose DETAIL rows are the
 * cart's lines.
 *
 * It carries the SKU and the QUANTITY and nothing else, deliberately. The
 * line's own `item_change` fills the description, the unit price and the tax
 * rate from the product master, so what is charged comes from the
 * application's own data and never from a basket the visitor can edit. That
 * is also why the sku has to be the key the line's column expects.
 */
export interface PageCartSource {
  /** The child form of the target transaction the lines belong to. */
  detailFormNo: string
  /** Column on that form receiving each line's `sku`. */
  skuColumn: string
  /** Column on that form receiving each line's quantity. */
  qtyColumn: string
  /** Booking lines only: column receiving the first date (ISO yyyy-mm-dd). */
  startColumn?: string
  /** Booking lines only: column receiving the last date / check-out. */
  endColumn?: string
  /** Booking lines only: column receiving the number of nights / days. */
  unitsColumn?: string
}

/**
 * A date-range purchase on an `add_to_cart` button — a hotel stay, a vehicle
 * or equipment rental. The visitor picks the range on a calendar; the line's
 * total is `price × quantity × units`.
 *
 * Presentation only, like the rest of the cart: the order is priced again
 * server-side, and AVAILABILITY is the application's business logic to check
 * on save. `blockedDates` greys days out for the visitor's benefit — bind it
 * to data through `{{vars}}` to reflect what is sold out.
 */
export interface CartBooking {
  /** `night`: priced per night, the end date is the check-out (exclusive).
   *  `day`: priced per day, the end date is the last day used (inclusive).
   *  Default `night`. */
  unit?: 'night' | 'day'
  /** "Check-in" / "Pick-up". */
  startLabel?: string
  /** "Check-out" / "Return". */
  endLabel?: string
  /** Shortest bookable range, in units. Default 1. */
  minUnits?: number
  /** Longest bookable range, in units. */
  maxUnits?: number
  /** How far ahead the start may be, in days. Default 365. */
  maxAdvanceDays?: number
  /** ISO dates that cannot be booked, separated by commas, spaces or new
   *  lines. Supports `{{vars}}`, so a page variable can supply them. */
  blockedDates?: string
  /** Pre-selected range — usually `{{checkin}}` / `{{checkout}}` from a
   *  search form, so every room on a results page starts on the same dates. */
  defaultStart?: string
  defaultEnd?: string
}

export interface PageInput {
  name: string                   // identifier referenced by blocks/filters/{{name}}
  label: string
  type: PageInputType
  // ── Source flags & configs (any combination allowed) ──
  isInput?: boolean              // render as a form control (default: true unless display is set)
  display?: boolean              // hide from input_group rendering; read-only
  api?: ApiVariableSource        // populates the variable from REST
  cart?: PageCartSource          // populates the variable from the page cart
  expression?: string            // computed default; supports {{var}} and re-evaluates on change
  // ── Initial value ──
  default?: string               // for range/date_range this is the LOW/start value
  default_to?: string            // for range/date_range this is the HIGH/end value
  /** Set by the publisher on a collection record's fields: the value is REAL
   *  even when empty (render '' rather than leave the `{{token}}` in place). */
  bound?: boolean
  // ── Input-control specific (only meaningful when isInput is on) ──
  options?: PageInputOption[]    // for select
  placeholder?: string
  // Numeric / range constraints — apply to number, spinner, percentage, slider, range
  min?: number
  max?: number
  step?: number
  unit?: string                  // optional suffix (e.g. "kg", "₹")
  validations?: ValidationRule[] // run by the action runner's `validate` step
  helpText?: string              // muted helper line under the input at runtime
  // ── Deprecated hint, retained for back-compat ──
  kind?: VariableKind
}

// Convenience alias — `PageVariable` is the friendlier name for the same shape.
export type PageVariable = PageInput

// ─── Per-input validation rules ────────────────────────

export type ValidationRuleKind =
  | 'required' | 'email' | 'url' | 'number'
  | 'regex' | 'min_length' | 'max_length'

export interface ValidationRule {
  rule: ValidationRuleKind
  arg?: string | number          // pattern for regex, length for min/max_length
  message?: string               // user-facing override
}

// Filter value source for embed filters
export type FilterSource = 'page_input' | 'static'

export interface FilterValueRef {
  source: FilterSource
  name?: string                  // when source === 'page_input', references PageInput.name
  value?: string                 // when source === 'static'
}

export type FilterOp =
  | 'eq' | 'neq'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'between'
  | 'contains' | 'starts_with'

export interface EmbedFilter {
  id: string
  column: string                 // column name in target transaction
  op: FilterOp
  value?: FilterValueRef         // single-value ops
  from?: FilterValueRef          // for between
  to?: FilterValueRef            // for between
}

// ─── Argument mapping (page input → target object parameter) ────────
// Used to pass values from the page into the destination of an embed
// (D/V) or a navigation. Each row maps a target column / criterion key
// to a source value (page input or static literal). A user-friendly
// label survives renames in the page so the mapping stays meaningful.

export interface ArgMapping {
  id: string
  target: string                 // target column / criterion key in destination
  label?: string                 // optional friendly label for the mapping row
  source: FilterValueRef         // page_input | static
}

// Canonical access mode — decides what an end user may DO with an embedded
// transaction, enforced server-side across ALL render modes:
//   'full'        — browse + add + edit + delete (today's 'list' behaviour)
//   'single_add'  — fill exactly ONE record (auto-added on save; no Add button,
//                   no browsing of existing records)
//   'multi_add'   — add multiple new records; no browsing of existing records
//   'edit'        — list + edit ONLY records within the designer filter scope;
//                   no header add / no header delete
export type NormalizedEmbedMode = 'full' | 'single_add' | 'multi_add' | 'edit'
// Wire type keeps the legacy 'list'/'add' values parseable on old page models;
// they normalise to the canonical values via `normalizeEmbedMode`.
export type EmbedMode = NormalizedEmbedMode | 'list' | 'add'

// Normalise an embed's authored mode + render template into the canonical
// access mode. MUST mirror backend `embed_access.derive_access_mode`.
export function normalizeEmbedMode(
  embed: Pick<EmbedConfig, 'mode' | 'renderMode' | 'captureTemplate'> | null | undefined,
): NormalizedEmbedMode {
  const m = (embed?.mode || '').trim()
  if (m === 'full' || m === 'single_add' || m === 'multi_add' || m === 'edit') return m
  if (m === 'edit') return 'edit'
  if (m === 'add') {
    const render = (embed?.renderMode || 'realtime').trim()
    const tpl = (embed?.captureTemplate || 'single-classic').trim()
    // Realtime add or a single-classic capture form = one-record entry.
    // Multi-* capture templates historically retrieve + edit existing rows, so
    // map them to 'full' to preserve published-page behaviour; authors opt into
    // the stricter 'multi_add' explicitly.
    if (render === 'realtime' || tpl === 'single-classic') return 'single_add'
    return 'full'
  }
  // 'list' or unset → browse everything.
  return 'full'
}

// What each canonical mode allows the UI to render. Server enforcement is the
// source of truth; this drives which affordances the client shows.
export interface EmbedModeCaps {
  browseExisting: boolean   // list / retrieve pre-existing records
  headerAdd: boolean        // "+ Add" / "Add row" / "Add record" affordances
  headerEdit: boolean       // edit an existing header record
  headerDelete: boolean     // delete a header record
  singleRecord: boolean     // exactly one record, single Save
}

export function embedModeCaps(mode: NormalizedEmbedMode): EmbedModeCaps {
  switch (mode) {
    case 'single_add':
      return { browseExisting: false, headerAdd: false, headerEdit: true,
               headerDelete: false, singleRecord: true }
    case 'multi_add':
      return { browseExisting: false, headerAdd: true, headerEdit: true,
               headerDelete: true, singleRecord: false }
    case 'edit':
      return { browseExisting: true, headerAdd: false, headerEdit: true,
               headerDelete: false, singleRecord: false }
    case 'full':
    default:
      return { browseExisting: true, headerAdd: true, headerEdit: true,
               headerDelete: true, singleRecord: false }
  }
}

// T-only — how the runtime renders an embedded transaction:
//   'realtime'  (default) — embed Vision's transaction UI live; realtime
//                form/list with selection events and chained filters.
//   'capture'   — render forms locally + persist via the live-preview
//                executor (real INSERT/UPDATE + server validations).
//                Supports list+filter+retrieve, multi-record batch save,
//                and the unsaved → saved row-state lifecycle. Works
//                offline (records queue locally) and syncs on next save.
//   'integrate' — same as 'capture' on the front-end. The only
//                difference is that the save target is configurable —
//                the default is the live-preview executor (same as
//                capture); an authored ``integrationUrl`` overrides it
//                to POST the batch to a 3rd-party endpoint instead.
//                Used when the smart page is a "form for someone else's
//                system" rather than a doc-bound editor.
export type EmbedRenderMode = 'realtime' | 'capture' | 'integrate'

// Capture-mode layout templates. Determines how many transaction
// records the embed manages and how they're presented:
//   'single-classic'    — one record (header form + detail grids). Default.
//   'multi-tabular'     — many records, each rendered as a row in a flat
//                         header grid laid out per the transaction
//                         metadata (column widths + x-order). Add/edit
//                         happens inline in the grid. Detail rows show
//                         in an expandable row body.
//   'multi-tab'         — many records, each occupying its own tab; tab
//                         body is the single-classic header+detail form.
//   'multi-accordion'   — like 'multi-tab' but vertical accordion sections
//                         instead of a tab strip.
//   'multi-form-input'  — list of records at top with status badges
//                         (retrieved/dirty/unsaved/saved); add+edit
//                         happen in a popup form. Useful when the
//                         transaction has many columns that don't
//                         fit a single row.
//   'multi-compact'     — mobile-style card list + full-screen single-column
//                         form (the Android app's transaction UX). A
//                         list-level interface like 'multi-form-input'; best
//                         for phone-width layouts.
//
// All templates save through processTransactionRequest with a batched
// {project, obj_name, records: [...]} payload so all records succeed or
// fail together. The multi-* templates additionally retrieve existing
// rows from the live-preview executor on mount so the user can edit them.
export type EmbedCaptureTemplate =
  | 'single-classic'
  | 'multi-tabular'
  | 'multi-tab'
  | 'multi-accordion'
  | 'multi-form-input'
  | 'multi-compact'

// Embed object types:
//   T = Transaction (list/add/edit a transaction form)
//   D = Dashboard   (render a dashboard by obj_name)
//   V = Visual      (render a single visual by obj_name)
//   S = Smart Page  (render another smart page inline; args map outer page
//                   inputs onto the embedded page's PageInput names)
//   G = General Process (run a process — criteria → preview → execute — inline,
//                   via the same Run surface the designer live-preview uses)
//   R = Report      (render a Jasper report inline via the shared ReportViewer;
//                   args map page inputs onto the report's prompt params)
//   U = Transaction Wizard (run a guided wizard inline — the shared
//                   WizardRunner; args map page inputs onto the wizard's
//                   declared inputs; its OWN grant decides what it may do,
//                   docs/plans/wizard-smart-page-embed.md)
export type EmbedObjType = 'T' | 'D' | 'V' | 'S' | 'G' | 'R' | 'U'

export interface EmbedConfig {
  obj_name: string               // target obj_name (transaction / dashboard / visual)
  obj_type: EmbedObjType
  mode: EmbedMode                // access mode (full/single_add/multi_add/edit) — applies to ALL render modes; server-enforced. Legacy 'list'/'add' normalise via normalizeEmbedMode.
  // Server-minted, page-bound token (external pages only). Not authored — the
  // page-model serve path stamps it; the embed client echoes it on every
  // site-txn / site-doc call so an anonymous visitor can use the form without
  // the transaction's login gate being relaxed for direct-URL callers.
  _embedToken?: string
  form_no?: string               // optional sql_models index for multi-form transactions
  filters: EmbedFilter[]         // T-list filters (column+op+value)
  args?: ArgMapping[]            // T add/edit pre-fills, D global-criteria, V criteria
  // T-only — defaults to 'realtime'. See EmbedRenderMode.
  renderMode?: EmbedRenderMode
  // Capture-only — overrides the default Save button label.
  captureSaveLabel?: string
  // Capture-only — chooses the layout template. Defaults to
  // 'single-classic' for backwards compatibility with existing
  // capture embeds. See EmbedCaptureTemplate.
  captureTemplate?: EmbedCaptureTemplate
  // Integrate-only — when set, the batch Save POSTs to this URL
  // (with the same {project, obj_name, records:[...]} payload the
  // live-preview process endpoint expects) instead of routing
  // through the live-preview executor. Used for 3rd-party form
  // integrations where the smart page is a thin authoring shell on
  // top of someone else's system. Headers / auth model is left to
  // the integration target. Unset → default (live-preview persistence).
  integrationUrl?: string
  // T-only — bind this embed's record state to a page variable
  // (typically a `type: 'json'` variable). The runtime derives the
  // embed's mode from the variable's contents:
  //   - empty / null / no `primary_key` field      → add mode (blank form)
  //   - has a `primary_key` field                  → edit mode, loads that record
  //   - has other fields but no `primary_key` yet  → add mode pre-filled
  //                                                  with those fields (a
  //                                                  "draft" that the user
  //                                                  was already editing)
  // Combined with an `api` source on the variable that fetches
  // `/api/<obj_name>/{{<id_input>}}` and `triggerOn: ["<id_input>"]`,
  // the same embed seamlessly flips between add and edit as the
  // identifying input changes — and an in-progress draft is preserved
  // until the user saves and the api re-populates the variable with
  // the assigned primary key.
  recordVar?: string
  // Selection → page variables. When the user clicks a row (T-list) or a
  // data row / card / tree node (V visual), the runtime writes a key value
  // and the full row payload into these page variables. Downstream blocks
  // (other embeds, variable display, filters, other visuals' args) then
  // reactively re-render / re-fetch against the new selection. Empty /
  // unset means "selection has no effect".
  //   selectedIdVar    → string variable receiving the row's key:
  //                       • T-list  : the row's _txn_id
  //                       • V visual: the value of `selectedKeyColumn`
  //                                   from the clicked row (defaults to the
  //                                   first column when unset)
  //   selectedDataVar  → json variable receiving the full clicked row
  // Either can be set independently; both are common when downstream
  // blocks need both the id (for filters / visual args) and the data
  // (for headings, summary cards, etc.).
  selectedIdVar?: string
  selectedDataVar?: string
  // V-only — which column of the clicked visual row supplies the value
  // written to selectedIdVar. Matched case-insensitively against the
  // visual's result columns (the keys of a jsonData row). When unset, the
  // first column's value is used. Ignored for T embeds (always _txn_id).
  selectedKeyColumn?: string
  // ── Grid UX features (apply to tabular template + realtime list) ──
  // All default ON when omitted, so existing embeds gain the toolbar
  // without per-block migration. Designer can disable any feature on a
  // per-embed basis from the property panel.
  gridUx?: EmbedGridUx
  // Per-column total/aggregate displayed in the grid footer. Keyed by
  // column db_name. Recomputes whenever the visible (filtered) row set
  // changes. See EmbedColumnTotal.
  columnTotals?: Record<string, EmbedColumnTotal>
  // ── Interface switching ───────────────────────────────────────────
  // When enabled (the default), runtime surfaces (in-app live preview,
  // published run, downloaded app) show a "View" switcher that lets the
  // viewer swap this transaction between its List / Multi-tabular entry /
  // Form-input interfaces at runtime. Ephemeral — never mutates the saved
  // config. Hidden on the design canvas (the interface is chosen in the
  // side panel there). Default-on: omitted/true = on, false = off.
  allowInterfaceSwitch?: boolean
  // ── Presentation ──────────────────────────────────────────────────
  // How the embed occupies the page:
  //   'inline' (default) — render the embed body directly in the page
  //                        flow (current behaviour).
  //   'modal'            — render a single trigger BUTTON; the embed body
  //                        (typically a capture add-form) opens in a modal
  //                        dialog when clicked. Use this for "Add feedback",
  //                        "Register", "Leave a review" style actions that
  //                        shouldn't occupy the page until invoked. Pairs
  //                        with renderMode:'capture' + mode:'add'.
  presentation?: 'inline' | 'modal'
  // Modal-only: label on the trigger button (default derives from mode,
  // e.g. "Add"). triggerVariant styles it like a ButtonSpec variant.
  triggerLabel?: string
  triggerVariant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  // Modal-only: heading shown at the top of the dialog.
  modalTitle?: string
  // Modal-only: confirmation message shown after a successful save; the
  // dialog auto-closes shortly after. Defaults to a generic thank-you.
  successMessage?: string
  // U-only — a `type: 'json'` page variable that receives
  // `{wizard, outcome, txn_id}` when a run of the embedded wizard ends, so
  // other blocks can react (refresh a list, show a thank-you section).
  resultVar?: string
}

// Toggles for built-in grid UX features. Omit a field to keep the
// default (true). Setting false hides the corresponding UI affordance.
export interface EmbedGridUx {
  search?: boolean       // global free-text search input in toolbar
  filter?: boolean       // per-column filter funnel + chip bar
  sort?: boolean         // sortable column headers
  fullScreen?: boolean   // expand/restore toggle
  totals?: boolean       // footer totals row (only renders when columnTotals has entries)
  keyboardNav?: boolean  // arrow-key cell navigation in tabular template
}

// Per-column footer aggregate.
//   kind: 'aggregate'  — built-in SUM/AVG/MIN/MAX/COUNT applied to this column
//   kind: 'expression' — Excel-like formula. Bare column db_names resolve to
//                        the column's SUM across the visible rows. Other
//                        aggregates are exposed as `<col>_avg`, `<col>_min`,
//                        `<col>_max`, `<col>_count`. Supports arithmetic
//                        (+ - * / ^), parens, and hot-formula-parser's
//                        built-in math/logic functions (ROUND, IF, IFERROR,
//                        ABS, AVERAGE, etc.).
export type EmbedColumnTotalKind = 'aggregate' | 'expression'
export type EmbedColumnAggregate = 'sum' | 'avg' | 'min' | 'max' | 'count'
export interface EmbedColumnTotal {
  kind: EmbedColumnTotalKind
  // For 'aggregate': one of EmbedColumnAggregate (defaults to 'sum').
  // For 'expression': the formula text.
  spec: string
  // Optional display prefix shown in the footer cell ("Total", "Net", etc.).
  label?: string
}

// ─── Shared style primitives ───────────────────────────

export type Align = 'left' | 'center' | 'right'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonSpec {
  id: string
  label: string
  url?: string                   // legacy: when on_click is absent, treated as external_url
  variant: ButtonVariant
  size?: ButtonSize
  icon?: string                  // lucide-style icon name (rendered as label prefix)
  newTab?: boolean
  on_click?: ButtonClick         // explicit click intent (preferred for new buttons)
}

// ─── Per-button click intent ───────────────────────────

export type ButtonClick =
  | { kind: 'none' }
  | { kind: 'external_url'; url: string; newTab?: boolean }
  | {
      kind: 'navigate'
      obj_type: string
      obj_name: string
      // T-only: which form action to land on (list / add / edit). Optional
      // for back-compat — navigate buttons that pre-date this field route
      // to the object's default landing screen.
      mode?: EmbedMode
      // T-only: optional sql_models index for multi-form transactions.
      form_no?: string
      // Arguments passed to the destination. For T this prefills add/edit
      // forms or filters list-mode; for D/V this maps to dashboard global
      // criteria / visual criteria rules.
      args?: ArgMapping[]
    }
  | { kind: 'set_inputs'; values: Record<string, string> }
  // Put something in the page cart. Client-side and session-free, so it
  // works on a PUBLISHED page — which is the point: `payment_checkout`
  // starts after an order exists and the Vision eCommerce blocks need an
  // ERP source, so until this there was no way to put anything in a basket
  // on a public storefront. `price` is what to SHOW; the amount actually
  // charged is always re-read from the saved record server-side.
  | {
      kind: 'add_to_cart'
      sku: string
      name: string
      /** As authored — "₹3,200". The leading non-digits become the
       *  currency prefix and the digits the figure used for totals. */
      price?: string
      image?: string
      /** Where the line links back to. */
      href?: string
      /** A second line under the name — "Orthopaedic · 30.5 cm". */
      meta?: string
      /** Units added per click — or, with `qtyPicker`, the picker's
       *  starting value. Default 1. */
      qty?: number
      /** Let the visitor choose how many before adding: renders − [n] +
       *  beside the button. */
      qtyPicker?: boolean
      /** Ceiling for the picker. Default 99. */
      maxQty?: number
      /** Caption over the picker — "Quantity", "Rooms", "Vehicles". */
      qtyLabel?: string
      /** Sell a DATE RANGE (hotel stay, vehicle / equipment rental): a
       *  calendar renders above the button and `price` is per night/day. */
      booking?: CartBooking
      /** Confirmation shown on the button for a moment after a click. */
      addedLabel?: string
      /**
       * What the button says once this item is already in the basket —
       * `{n}` is the quantity there. Default "In cart · {n}". `""` turns
       * the in-cart state off (the button always reads its own label).
       */
      inCartLabel?: string
      /**
       * Once the item is in the basket, swap the button for a live − n +
       * stepper that edits THAT cart line (0 removes it). Default true —
       * false when `qtyPicker` or `booking` is set, which have their own
       * controls.
       */
      inCartStepper?: boolean
      /** A small "Added to cart · View cart" notice after a click. Default true. */
      toast?: boolean
      /** The notice's text. Default "Added to cart". */
      toastLabel?: string
      /** The notice's link text. Default "View cart". */
      toastLinkLabel?: string
      /** Where the notice links (the cart page, '/cart'). No link when unset. */
      cartHref?: string
      /** A heart beside the button that saves the same item to the wishlist. */
      wishlist?: boolean
      /** Accessible name / tooltip of that heart. Defaults "Add to wishlist" / "Saved to wishlist". */
      wishlistAddLabel?: string
      wishlistRemoveLabel?: string
    }
  | { kind: 'run_action'; actionName: string }
  // Smooth-scroll to an anchor on the same page. `target` is a Section /
  // Block `anchorId` (with or without a leading '#'). `offset` compensates
  // for a sticky header (px, subtracted from the target's top).
  | { kind: 'scroll_to'; target: string; offset?: number; smooth?: boolean }
  // Open the modal of a `popup`-tagged Section by its section id. Until this
  // existed only the pop-up's OWN trigger could open it.
  | { kind: 'open_popup'; sectionId: string }
  // Flip a page variable between two values (defaults false ⇄ true) — the
  // primitive behind "show more", tab-like reveals and any `{{var}}`-gated
  // visibility rule.
  | { kind: 'toggle_var'; name: string; on?: string; off?: string }
  // Copy text (supports `{{var}}`) to the clipboard, with a transient
  // confirmation on the element.
  | { kind: 'copy'; text: string; confirm?: string }
  // Play / pause a `video` block by its block id.
  | { kind: 'play_media'; blockId: string; action?: 'play' | 'pause' | 'toggle' }
  // End the visitor's session (the same sign-out as the `auth_menu`), then
  // go to `redirect` (a site path; default: reload the current page).
  | { kind: 'sign_out'; redirect?: string }

// 'none'        → no `background` declaration emitted (the element inherits /
//                 shows whatever is behind it, but does NOT override a base
//                 surface fill a block may set on itself).
// 'transparent' → emits `background: transparent` EXPLICITLY, so it overrides
//                 any default surface fill — use this to force a block to show
//                 the page background through it.
// 'video'       → a muted, looping, autoplaying background video. `posterUrl`
//                 paints first and is the ONLY thing the static SSR / a
//                 reduced-motion visitor sees, so it must be a real frame.
// 'gradient_animated' → the gradient stops drift slowly via CSS keyframes.
export type BackgroundKind =
  | 'none' | 'transparent' | 'color' | 'gradient' | 'image'
  | 'video' | 'gradient_animated'

// One stop of a multi-stop gradient. `at` is the position in percent.
export interface GradientStop {
  color: string
  at?: number
}

export interface Background {
  kind: BackgroundKind
  color?: string                 // for kind=color
  gradientFrom?: string          // for kind=gradient (2-stop shorthand)
  gradientTo?: string
  gradientAngle?: number         // degrees
  // Multi-stop / radial gradients. When `stops` has 2+ entries it REPLACES
  // the gradientFrom/gradientTo pair. `gradientType` picks linear (default)
  // vs radial; `gradientShape` positions a radial gradient's center.
  stops?: GradientStop[]
  gradientType?: 'linear' | 'radial'
  gradientShape?: 'center' | 'top' | 'bottom' | 'left' | 'right'
                | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  imageUrl?: string              // for kind=image
  /**
   * A wash over the image, for legibility. A single colour covers the
   * whole frame evenly — fine over a busy photograph, wrong when the
   * point is to keep one side of the picture visible, which is what a
   * hero usually wants. `overlayGradient` takes the same shape as a
   * gradient background and is laid over the image instead.
   */
  overlay?: string               // optional rgba overlay over image
  overlayGradient?: {
    from: string
    to: string
    angle?: number               // degrees; default 180 (top → bottom)
    stops?: GradientStop[]       // 2+ stops REPLACE from/to
  }
  // kind=video: the looping source + its poster frame (required — it is the
  // fallback on SSR, on reduced-motion, and before the video buffers).
  videoUrl?: string
  posterUrl?: string
  // Parallax (kind=image only): the background image stays pinned to the
  // viewport while the page scrolls over it, via `background-attachment:
  // fixed`. Chosen for reliability — it paints the image directly on the
  // element (so it can never go blank), needs no global stylesheet and no
  // scroll-driven-animation support, and works in every desktop browser.
  // Mobile browsers ignore `fixed` and degrade to a static cover image.
  // (An earlier implementation used `animation-timeline: view()`; it was
  // withdrawn because browser support is uneven. Do not reintroduce it —
  // see docs/smart-page-motion-and-interactivity-plan.md §2.2.)
  parallax?: boolean
  parallaxIntensity?: number     // retained for back-compat; unused by the fixed-attachment technique
}

export type ColumnsCount = 1 | 2 | 3 | 4 | 5 | 6

// ─── Block schema ──────────────────────────────────────

export type BlockType =
  // text
  | 'heading'
  | 'paragraph'
  | 'bullet_list'
  | 'numbered_list'
  | 'todo'
  | 'quote'
  | 'code'
  | 'divider'
  // media
  | 'image'
  | 'video'
  | 'gallery'
  | 'logo_cloud'
  | 'map'
  // layout / sections
  | 'hero'
  | 'cta_banner'
  | 'spacer'
  | 'columns'
  | 'container'
  | 'card'
  | 'card_grid'
  // content sections
  | 'feature_grid'
  | 'stats'
  | 'countdown'
  | 'timer'
  | 'testimonial'
  | 'pricing'
  | 'faq'
  | 'tabs'
  | 'accordion'
  | 'callout'
  | 'timeline'
  // action
  | 'button'
  | 'button_group'
  // form
  | 'newsletter'
  | 'contact_form'
  // app
  | 'input_group'
  | 'embed'
  | 'file_upload'
  // v2 additions
  | 'form'
  | 'custom_code'
  | 'custom_html'
  | 'nav_menu'
  | 'breadcrumb'
  | 'filter_bar'
  | 'cart_badge'
  | 'cart_lines'
  | 'cart_summary'
  | 'wishlist_button'
  | 'wishlist_badge'
  | 'wishlist_lines'
  | 'table'
  | 'variable'
  | 'notepad'
  | 'rich_content'
  | 'collection_search'
  | 'comments'
  | 'chatbot'
  | 'auth_form'
  | 'auth_menu'
  // marketing sections (Tier 6)
  | 'bento_grid'
  | 'marquee'
  | 'counter'
  | 'before_after'
  | 'carousel'
  | 'team'
  | 'steps'
  | 'comparison_table'
  | 'social_links'
  | 'lottie'
  | 'cookie_consent'
  | 'floating_button'
  // eCommerce — Proteus Vision integration. Each block hides its own
  // ERP plumbing (cascading product/style/preferences/measurements,
  // customer search, order create) behind a small attribute surface.
  | 'entity_picker'
  | 'new_customer_form'
  | 'product_selection'
  | 'product_preview'
  | 'order_summary'
  | 'order_submit'
  // The application's OWN checkout — money to the project owner's merchant
  // account, configured in Advanced → Payments. Independent of the Vision
  // blocks above: it charges for a record the application already holds.
  | 'payment_checkout'
  | 'payment_settings'
  // Storefront commerce blocks with their own server doors (checkout,
  // order history, reviews) — see the interfaces at the end of this file.
  | 'cart_checkout'
  | 'my_records'
  | 'saved_records'
  | 'reviews'

// ─── Chatbot block ──────────────────────────────────────
// An end-user chatbot grounded on the page's content + (RBAC-gated) project
// data. The runtime /chat endpoint does the grounding + LLM call; this block
// just configures scope + presentation.
export interface ChatbotBlock extends BaseBlock {
  type: 'chatbot'
  title?: string
  greeting?: string                // first assistant message shown on load
  systemPrompt?: string            // overrides the default assistant instructions
  groundOnPage?: boolean           // include this page's text as context (default true)
  dataSources?: string[]           // obj_names the bot may read (each RBAC-gated)
  suggestedQuestions?: string[]    // quick-start prompts shown as chips
  height?: number                  // px; default 420
}

// ─── Auth form block ─────────────────────────────────────
// A sign-in / sign-up form an author can place on a smart page to build a
// branded login screen (vs. the runtime's built-in screen). The deployed
// runtime wires it to the real auth flow; in the designer/SSR it renders as a
// visual form. Designate the page holding it as the project's login page via
// `application.auth.loginPage`.
export interface AuthFormBlock extends BaseBlock {
  type: 'auth_form'
  /**
   * `identify` = identify-first: the visitor types an email OR mobile number,
   * then logs in with a password, or (unknown + self-registration allowed) the
   * account is created on the spot, signed in, and a set-password link sent.
   * Rendered by page-runtime `IdentifyStep`.
   */
  mode?: 'login' | 'register' | 'identify'
  /** Label of the email / mobile-number (sign-in identifier) field. */
  identifierLabel?: string
  /** LEGACY — registration now always asks for email AND mobile number, so
   *  the mobile field is shown on sign-up whatever this says. */
  showPhone?: boolean
  heading?: string
  subheading?: string
  submitLabel?: string
  showTwasta?: boolean
  /**
   * Offer a link that flips the form between sign-in and sign-up in place.
   * Default true when the project allows self-registration.
   */
  showRegisterLink?: boolean
  /** Label of that link. Defaults derive from `mode`. */
  registerLinkLabel?: string
  /** Optional "forgot password?" link target. */
  forgotHref?: string
  /**
   * Show "Sign in" / "Create account" TABS at the top of the form (any mode).
   * The sign-in tab is `mode`'s own flow (identify-first or password); the
   * create-account tab is the full sign-up form below. A link to the page
   * with `?mode=register` opens on the create-account tab. Default false.
   */
  tabs?: boolean
  /** Tab captions. Defaults "Sign in" / "Create account". */
  signInTabLabel?: string
  registerTabLabel?: string
  /** Muted line under the sign-in step (e.g. what happens to a new customer). */
  signInNote?: string
  /** Sign-up form wording. Default heading "Create account". */
  registerHeading?: string
  registerSubheading?: string
  registerSubmitLabel?: string
  /**
   * Name on the sign-up form: `split` = first + last name, `single` = one
   * full-name field, `none` (default) = no name field. Saved as the account's
   * display name.
   */
  nameFields?: 'none' | 'single' | 'split'
  /**
   * Sign-up identity: `username` (default — a username plus an email) or
   * `contact` — an email and/or a mobile number, at least one required, and
   * the account is keyed by whichever is given (email preferred).
   */
  registerIdentity?: 'username' | 'contact'
  /** Calling code shown before the mobile field (e.g. "+91"); a number typed
   *  without one is sent with it. */
  phoneCountryCode?: string
  /** Placeholder of the sign-in identifier (email / mobile) field. */
  identifierPlaceholder?: string
  /** Placeholder text for the sign-up fields. */
  emailPlaceholder?: string
  phonePlaceholder?: string
  /** Line under the password field; a strength bar is always shown. */
  passwordHint?: string
  /**
   * Links for a required "I agree to the Terms and Privacy policy" box on the
   * sign-up form. The box appears when either is set.
   */
  termsHref?: string
  privacyHref?: string
  /** A highlighted note under the create-account button (e.g. an invitation
   *  to a partner programme). `href` makes the body a link. */
  registerNote?: { title?: string; body?: string; href?: string; icon?: string }
  style?: BlockStyle
}

// ─── Auth menu block ─────────────────────────────────────
// A compact authentication affordance for a site header/footer (vs.
// `auth_form`, which is a full sign-in page). When the visitor is signed out
// it shows "Log in" (and optionally "Sign up") buttons that route to the
// site's login flow. When signed in it shows the user's avatar; clicking it
// opens a dropdown with the user's identity, a self-service account panel
// (change password + profile-image upload), any author-configured extra
// links, and "Sign out". Auth-agnostic like `auth_form`: each surface wires
// it to the real session via an adapter; in the designer/SSR it renders the
// signed-out state as a visual-only preview.
export interface AuthMenuLink {
  id: string
  label: string
  /** Site-internal "/slug" (resolved per surface) or an absolute URL. */
  href?: string
  /** Optional Material icon name shown beside the label. */
  icon?: string
  /** A status chip after the label — a `{{token}}` (e.g. `{{ds_partner.STATUS}}`
   *  from a data source on the header page). Hidden while it resolves empty. */
  badge?: string
  /** Words for the badge's resolved values (`{"PENDING": "In review"}`); a
   *  value with no entry shows as it is. */
  badgeLabels?: Record<string, string>
  badgeTones?: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'>
}

export interface AuthMenuBlock extends BaseBlock {
  type: 'auth_menu'
  /** Signed-out call-to-action labels. Defaults: "Log in" / "Sign up". */
  loginLabel?: string
  signupLabel?: string
  /** Show the secondary "Sign up" button when signed out (default true). */
  showSignup?: boolean
  /** Horizontal placement of the cluster within its block (default 'right'). */
  align?: Align
  /** Extra links appended to the signed-in dropdown menu. */
  links?: AuthMenuLink[]
  /** Show the "My account" item (identity + change password). Default true. */
  showProfile?: boolean
  /** Allow changing password from the account panel (default true). */
  showChangePassword?: boolean
  /** Allow uploading a profile image from the account panel (default true). */
  allowAvatarUpload?: boolean
  style?: BlockStyle
}

// ─── Pop-up section config ───────────────────────────────
// A section tagged `popup` is NOT rendered in the page flow. Instead it
// renders a single TRIGGER (a floating button, an inline link, or an
// icon-only badge); clicking it opens the section's own blocks in a modal
// dialog. Authors add blocks to a pop-up section exactly like any other
// section — whatever they add (a chatbot, an embed of a settings page,
// text, a form, …) becomes the modal content. The trigger decides the
// affordance style and its on-screen placement; this config lives on the
// Section (`section.popup`), not on a block.
//   style 'fab'  — floating pill button anchored to a screen corner
//   style 'link' — inline link/button rendered in the page flow
//   style 'icon' — circular icon-only badge anchored to a corner
// Pop-ups need runtime JS (and chatbot/embed need APIs), so the static SSR
// prerender skips `popup` sections entirely; they render on the interactive
// surfaces (designer preview, in-app + standalone runtime).
// Where a corner-anchored trigger (fab / icon) sits on screen.
export type PopupCorner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
// Trigger button size preset.
export type PopupTriggerSize = 'sm' | 'md' | 'lg'
// Trigger button outline shape. `pill` = fully rounded, `rounded` = soft
// corners, `circle` = perfectly round (best for icon-only).
export type PopupTriggerShape = 'pill' | 'rounded' | 'circle'

export interface PopupTrigger {
  style: 'fab' | 'link' | 'icon'
  label?: string                 // fab / link text (ignored for icon-only)
  icon?: string                  // lucide-style icon name
  variant?: ButtonVariant        // reuses the shared ButtonVariant styling
  position?: PopupCorner         // fab + icon only
  offset?: number                // px from the anchored corner (default 24)
  // ── Look & feel (all optional; sensible defaults preserve prior look) ──
  size?: PopupTriggerSize        // button scale (default 'md')
  shape?: PopupTriggerShape      // outline shape (fab default 'pill', icon default 'circle')
  bgColor?: string               // custom background — overrides the variant's bg/border
  textColor?: string             // custom text/icon color — overrides the variant's color
  iconSize?: number              // px glyph size (default scales with `size`)
  shadow?: boolean               // drop shadow on fab/icon (default true)
  pulse?: boolean                // subtle attention-pulse animation on the trigger
}

// Where the modal sits within the viewport. `center`/`top`/`bottom` are
// floating dialogs; `left`/`right` are full-height side drawers.
export type PopupModalPosition = 'center' | 'top' | 'bottom' | 'left' | 'right'
// Modal entrance animation when the trigger is clicked. `slide` direction is
// derived from `modalPosition` (drawer slides in from its edge, sheets slide up).
export type PopupAnimation = 'none' | 'fade' | 'scale' | 'slide'

/**
 * Where a section came from when it was cloned from a live website — the
 * band's stable key on the source page and a fingerprint of what it said and
 * showed. A re-sync compares these with a fresh capture (changed on the live
 * site?) and with the section as drafted (edited in Twasta?). A section with
 * no `source` was authored in Twasta and is never touched by a re-sync.
 */
export interface SectionSource {
  url?: string
  key?: string
  fingerprint?: string
  /** Fingerprint of the section exactly as the clone wrote it. */
  drafted?: string
  crop?: string
  /** Made by the clone (a form band, a pop-up), not read off the source. */
  synthetic?: boolean
}

export interface SectionPopup {
  trigger: PopupTrigger
  modalTitle?: string
  modalSize?: 'sm' | 'md' | 'lg' // dialog max-width: 480 / 720 / 960 px
  modalPosition?: PopupModalPosition  // viewport placement (default 'top')
  animation?: PopupAnimation          // entrance animation (default 'scale')
}

// ─── Block sizing (px or %) ─────────────────────────────
// When set, the block's outer wrapper uses these dimensions. width:
// 'auto' or undefined falls back to the natural flow width; height is
// optional and applies to the rendered block body.

export type SizeUnit = 'px' | '%'

export interface BlockDimension {
  value: number
  unit: SizeUnit
}

export interface BlockSize {
  width?: BlockDimension
  height?: BlockDimension
}

// ─── Per-block style overrides ─────────────────────────
// Visual overrides applied to the block's outer wrapper. None of these
// replace block-specific style fields (e.g. HeroBlock.background) — they
// stack on top, so the wrapper can paint a card-like surface around any
// block type. All fields are optional and default to "no override".

export interface BlockBorder {
  width: number                  // px
  color: string
  style?: 'solid' | 'dashed' | 'dotted'
}

export interface BlockSpacing {
  top?: number
  right?: number
  bottom?: number
  left?: number
}

export type ShadowPreset = 'none' | 'sm' | 'md' | 'lg' | 'xl'

// Hover/focus tooltip attachable to any block. Pure CSS on every surface
// (no JS, so the static site keeps it), revealed on `:hover`/`:focus-within`.
export interface BlockTooltip {
  title: string
  body?: string
  placement?: 'top' | 'bottom' | 'left' | 'right'   // default 'top'
}

// Per-block typography overrides. `BlockStyle` used to carry only
// `textColor`, so an author could not make ONE heading display-sized or set
// a tighter measure on a single paragraph without a custom class. Each field
// maps 1:1 to its CSS property on the block wrapper (and therefore cascades
// into the block's own text nodes).
export interface BlockTypography {
  fontSize?: number              // px
  fontWeight?: number            // 100..900
  letterSpacing?: number         // em (e.g. -0.02)
  lineHeight?: number            // unitless
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize'
  fontFamily?: FontFamilyPreset  // overrides the page's body/heading family
  maxWidth?: number              // px — measure control for paragraph-carrying blocks
  // Paint the text with a gradient (`background-clip: text`). Falls back to
  // the resolved text color where unsupported.
  textGradient?: { from: string; to: string; angle?: number }
  /** Any font family by name (a cloned site's Verdana) — overrides `fontFamily`. */
  customFontFamily?: string
}

export interface BlockStyle {
  background?: Background        // matches the existing Background union
  textColor?: string
  padding?: BlockSpacing         // px on each side
  margin?: BlockSpacing          // px on each side — NEGATIVE values are allowed
                                 // and are how blocks overlap a section boundary
  border?: BlockBorder
  borderRadius?: number          // px
  /** Break out of the content column to the full viewport width — an
   *  edge-to-edge photo slider / band whose picture is CONTENT (a block), not
   *  the section's background. Use inside a `width: "full"` section. */
  bleed?: boolean
  shadow?: ShadowPreset
  align?: Align                  // overrides text-align of the wrapper
  className?: string             // optional escape hatch for custom CSS
  // ── Interaction states (emitted as REAL CSS — `:hover` / `:focus-within`
  //    are inexpressible inline, so a block carrying these is rendered
  //    through the wrapper's scoped-<style> path). ──
  hover?: BlockStyleState        // applied on :hover and :focus-within
  transition?: number            // ms for the state transition; default 200
  /**
   * WHAT the hover state applies to. Load-bearing on any block that renders
   * REPEATED items — a `card_grid` is ONE block containing many cards, so a
   * wrapper-scoped hover lifts the whole grid at once instead of the card
   * under the cursor, which is almost never what the author meant.
   *
   *   'block' — the block's outer wrapper (the default, and the only
   *             sensible choice for a single-unit block like `image`).
   *   'items' — each repeated child independently: every card in a card
   *             grid, tile in a bento grid, tier in a pricing table, photo
   *             in a gallery, member in a team block, and so on. This is
   *             what you want on a grid.
   *   'both'  — the wrapper AND each item. Use sparingly; two things moving
   *             at once usually reads as a bug rather than an effect.
   *
   * Omitted = 'block', so pages saved before this existed are unchanged.
   * The editor defaults NEW hover states on item-bearing blocks to 'items'.
   */
  hoverScope?: HoverScope
  // ── Layering (the "designed, not generated" primitives) ──
  zIndex?: number                // stacking order; pairs with negative margins
  backdropBlur?: number          // px — glassmorphism over imagery/video
  opacity?: number               // 0..1
  // ── Typography ──
  typography?: BlockTypography
}

/** What a `hover` state targets. See `BlockStyle.hoverScope`. */
export type HoverScope = 'block' | 'items' | 'both'

/**
 * Block types that render REPEATED elements, and the author-facing noun for
 * one of them.
 *
 * Any block listed here marks each repeated element with the `pg-item` class
 * at render time, which is what `hoverScope: 'items'` targets. The label is
 * used by the editor so the control reads "Each card" / "Each tier" / "Each
 * photo" rather than a generic "Each item".
 *
 * Adding a new multi-element block means adding it here AND putting
 * `pg-item` on its repeated element in the renderer — the two together are
 * what make per-element hover work.
 */
export const ITEM_BEARING_BLOCKS: Record<string, string> = {
  card_grid: 'card',
  feature_grid: 'feature',
  gallery: 'photo',
  pricing: 'tier',
  stats: 'stat',
  counter: 'counter',
  team: 'member',
  bento_grid: 'tile',
  testimonial: 'quote',
  steps: 'step',
  logo_cloud: 'logo',
  timeline: 'entry',
  faq: 'question',
  accordion: 'panel',
  tabs: 'tab',
  marquee: 'item',
  comparison_table: 'row',
  carousel: 'slide',
  button_group: 'button',
  social_links: 'icon',
  nav_menu: 'link',
  columns: 'column',
  table: 'row',
  bullet_list: 'item',
  numbered_list: 'item',
  todo: 'item',
}

/** True when this block type renders repeated elements hover can target. */
export function blockHasItems(type: string): boolean {
  return type in ITEM_BEARING_BLOCKS
}

/** Author-facing singular noun for one repeated element of this block. */
export function blockItemNoun(type: string): string {
  return ITEM_BEARING_BLOCKS[type] || 'item'
}

// The subset of BlockStyle a `:hover` state may override. Deliberately NOT
// `Partial<BlockStyle>`: nesting hover-in-hover or a state that swaps
// `className` has no meaning, and keeping it flat means the CSS emitter can
// never recurse.
export interface BlockStyleState {
  background?: Background
  textColor?: string
  border?: BlockBorder
  borderRadius?: number
  shadow?: ShadowPreset
  opacity?: number
  backdropBlur?: number
  /** Vertical lift in px (negative = up). The classic card-hover effect. */
  translateY?: number
  /** Uniform scale factor, e.g. 1.03. */
  scale?: number
}

// Per-breakpoint style overrides. The block's own `style` is the base
// (desktop / "all"); `tablet` and `mobile` carry only the fields that
// differ at narrower widths. Resolution cascades CSS-style: mobile
// inherits tablet inherits desktop. Runtimes emit real @media rules;
// the designer canvas resolves the layer for the device being previewed.
// See resolveBlockStyleFor / blockResponsiveCss in @twasta/page-runtime.
export interface ResponsiveStyle {
  tablet?: BlockStyle
  mobile?: BlockStyle
}

export interface BaseBlock {
  id: string
  type: BlockType
  size?: BlockSize               // optional explicit width/height (px or %)
  style?: BlockStyle             // optional per-block style overrides (base / desktop)
  responsive?: ResponsiveStyle   // optional per-breakpoint style overrides (tablet / mobile)
  // ── v2 additions (all optional; ignored by v1 readers) ──
  children?: Block[]             // populated only on container-capable blocks
  visibility?: BlockVisibility   // device / role / schedule / condition gating
  animation?: BlockAnimation     // entry / scroll / load animation
  // Anchor target for `scroll_to` clicks and `#hash` links. Emitted as the
  // wrapper element's `id`, so it also works as a plain in-page anchor for a
  // crawler and for the browser's own hash navigation.
  anchorId?: string
  // Click intent for the WHOLE block — the same union buttons use. This is
  // what makes a card, an image or a stat tile clickable without dropping to
  // `custom_code`. When set, the wrapper becomes a keyboard-focusable
  // `role="link"`/`role="button"` element.
  on_click?: ButtonClick
  // Hover/focus tooltip shown above the block. `title` is the bold line,
  // `body` the optional detail.
  tooltip?: BlockTooltip
  bindings?: BlockBindings       // dynamic-source map: settingPath → BindingExpr
  repeat?: BlockRepeat           // repeat this block's item template over a list data source
  /**
   * Conditional style: each rule whose `when` holds (same expression syntax
   * as `visibility.condition`, row-aware inside a repeat) layers its `style`
   * over the block's own, in order. Re-evaluated as the clock ticks when a
   * rule reads an `|age_min` / `|age_sec` token.
   */
  styleRules?: BlockStyleRule[]
  i18nKeys?: Record<string, string> // localization key map (settingPath → bundle key)
  // ── Document-mode additions (Notion-like document instances) ──
  // When the page's `pageMode === 'document'`, blocks marked
  // `documentEditable` accept user input at runtime; their per-instance
  // values are stored on the Document, not on the page template.
  documentEditable?: boolean
  documentRequired?: boolean
  // List/todo/table-row containers may be marked `documentExtensible` so
  // end-users can add or remove rows/items inside the document instance.
  documentExtensible?: boolean
  // Container-only flag for `kind='document'` pages: when true, the
  // runtime renders an "Add block" affordance INSIDE this container so
  // end-users can append entirely new child blocks (paragraphs, lists,
  // etc.) to the document instance. Distinct from `documentExtensible`
  // which only adds rows/items to the container's own collection.
  // Ignored unless the page also has `runtimeAddable === true`.
  runtimeAddable?: boolean
  // Per-block opt-in for runtime deletion. Read by the runtime ONLY
  // when the page's `runtimeDeletable === 'marked'`. Other policies
  // ('none' | 'added_only' | 'empty' | 'any') ignore this flag and
  // decide per their own rule.
  runtimeDeletable?: boolean
}

// ── Text blocks ──
// `text` is the canonical plain-text value (kept for back-compat, search,
// and as the fallback when a runtime can't render HTML). When the author
// applies inline formatting (bold / italic / link / inline color / code)
// the editor also stores the marked-up `html`; renderers prefer `html`
// when present and fall back to escaping `text`. Both still support
// `{{var}}` substitution. See `inlineRichHtml` in @twasta/page-runtime.
export interface HeadingBlock extends BaseBlock {
  type: 'heading'
  /**
   * The small-caps label above the heading. `hero`, `feature_grid`,
   * `steps`, `bento_grid` and `cta_banner` have had one for a while; a
   * section led by a plain heading had nowhere to put it, so authors
   * hand-rolled a styled paragraph and it drifted from the
   * `--pg-eyebrow-*` tokens the moment anyone changed them.
   */
  eyebrow?: string
  level: 1 | 2 | 3
  text: string
  html?: string
}
export interface ParagraphBlock extends BaseBlock {
  type: 'paragraph'
  text: string
  html?: string
}
// Multi-item bulleted / numbered lists. Each block holds an ordered
// array of items so authors can compose a full list inside one block
// (Enter splits, Backspace at start of an empty item removes it). The
// legacy single-`text` shape is preserved as optional fields so older
// persisted pages migrate transparently via `migrateListBlock`.
export interface ListItem {
  id: string
  text: string
  indent?: number
  /** A picture (SVG/PNG icon) shown in place of the bullet. */
  iconUrl?: string
}
export interface BulletListBlock extends BaseBlock {
  type: 'bullet_list'
  items: ListItem[]
  // legacy fields — accepted for backward compat
  text?: string
  indent?: number
}
export interface NumberedListBlock extends BaseBlock {
  type: 'numbered_list'
  items: ListItem[]
  text?: string
  indent?: number
}
export interface TodoItem {
  id: string
  text: string
  checked: boolean
}
export interface TodoBlock extends BaseBlock {
  type: 'todo'
  items: TodoItem[]
}
export interface QuoteBlock extends BaseBlock {
  type: 'quote'
  text: string
  html?: string
}
export interface CodeBlock extends BaseBlock {
  type: 'code'
  text: string
  language?: string
}
export interface DividerBlock extends BaseBlock {
  type: 'divider'
}

// ── Media ──

// Fixed aspect ratios an image/media frame can be locked to. Locking the box
// is what removes cumulative layout shift: the space is reserved before the
// bytes arrive.
export type MediaAspect = 'auto' | '1:1' | '4:3' | '3:2' | '16:9' | '3:4' | '2:3' | '21:9'

// Decorative clip-path shapes for an image.
export type ImageMask = 'none' | 'circle' | 'squircle' | 'blob' | 'arch' | 'diamond' | 'hexagon'

// Hover treatments for an image (all ride the shared hover/transition
// mechanism, so they honour `prefers-reduced-motion`).
export type ImageHoverEffect = 'none' | 'zoom' | 'lift' | 'tilt' | 'grayscale-off' | 'reveal-caption'

export interface ImageBlock extends BaseBlock {
  type: 'image'
  url: string
  alt?: string
  caption?: string
  rounded?: boolean
  shadow?: boolean
  align?: Align
  maxWidth?: number              // px
  // ── Framing (Tier 5A) ──
  aspect?: MediaAspect           // locks the box → no layout shift
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  objectPosition?: string        // e.g. 'center', 'top left', '50% 20%'
  mask?: ImageMask
  hoverEffect?: ImageHoverEffect
  // Intrinsic pixel dimensions, when known. Emitted as width/height
  // attributes so the browser reserves the right box before load.
  naturalWidth?: number
  naturalHeight?: number
  // Above-the-fold images (a hero) should opt OUT of lazy loading and get a
  // <link rel="preload">. Everything else lazy-loads by default.
  priority?: boolean
}
export interface VideoBlock extends BaseBlock {
  type: 'video'
  provider: 'youtube' | 'vimeo' | 'file'
  url: string
  aspect?: '16:9' | '4:3' | '1:1'
  caption?: string
}
export type MapProvider = 'leaflet' | 'google' | 'mapbox'
export type MapLocationMode = 'coordinates' | 'address'
export interface MapBlock extends BaseBlock {
  type: 'map'
  provider: MapProvider          // 'leaflet' = keyless OSM (default)
  apiKey?: string                // required when provider is google or mapbox
  locationMode: MapLocationMode
  latitude?: number              // used when locationMode='coordinates'
  longitude?: number
  address?: string               // used when locationMode='address' (geocoded at runtime)
  zoom?: number                  // 1..20, defaults to 14
  height?: number                // px, defaults to 400
  title?: string
  caption?: string
}
export interface GalleryItem {
  id: string
  url: string
  caption?: string
}
export interface GalleryBlock extends BaseBlock {
  type: 'gallery'
  items: GalleryItem[]
  columns: ColumnsCount
  rounded?: boolean
  gap?: number
  // ── Tier 5A ──
  // 'grid' (default) — equal-height tiles. 'masonry' — CSS columns, so tall
  // and wide images keep their own proportions.
  layout?: 'grid' | 'masonry'
  // Click a tile to open it full-size in an overlay. Pure CSS on the static
  // site (`:target`), JS-driven with arrow-key paging on interactive surfaces,
  // and always degrading to a plain link to the image.
  lightbox?: boolean
  aspect?: MediaAspect           // tile aspect in grid mode (default 4:3-ish box)
  hoverEffect?: ImageHoverEffect
}
export interface LogoItem {
  id: string
  name?: string
  url: string                    // image url
  href?: string                  // optional link
}
export interface LogoCloudBlock extends BaseBlock {
  type: 'logo_cloud'
  title?: string
  items: LogoItem[]
  monochrome?: boolean
  background?: Background
  // 'row' (default) — a static wrapping flex row.
  // 'marquee'       — the logos scroll continuously (CSS keyframes only, so
  //                   the static site gets it too). Pauses on hover and is
  //                   suppressed entirely under `prefers-reduced-motion`,
  //                   where it degrades to the static row.
  mode?: 'row' | 'marquee'
  marqueeSpeed?: number          // seconds for one full pass; default 30
  marqueeDirection?: 'left' | 'right'
  logoHeight?: number            // px; default 32
}

// ── Layout / sections ──

// A single media slide in a hero carousel. `type: 'image'` uses `url` as the
// image src; `type: 'video'` uses `url` + `provider` (youtube/vimeo embed, or a
// direct file played muted+looped).
export interface HeroSlide {
  id: string
  type: 'image' | 'video'
  url: string
  provider?: 'youtube' | 'vimeo' | 'file'   // video only; default 'file'
}

// Where the hero text/CTA block sits over a full-bleed carousel. `x` is the
// horizontal anchor, `y` the vertical one (a 3×3 grid). `align` still controls
// the text alignment within the content block.
export interface HeroContentPosition {
  x: 'left' | 'center' | 'right'
  y: 'top' | 'center' | 'bottom'
}

export interface HeroBlock extends BaseBlock {
  type: 'hero'
  eyebrow?: string
  title: string
  subtitle?: string
  primaryCta?: ButtonSpec
  secondaryCta?: ButtonSpec
  align: Align
  height?: 'compact' | 'normal' | 'tall' | 'screen'
  background: Background
  imageUrl?: string              // optional side image (when no slides)
  imageSide?: 'left' | 'right' | 'none'
  textColor?: string
  // Media carousel: when `slides` has 1+ entries the hero renders them
  // full-bleed behind the content (overriding the side-image layout), with
  // auto-advancing dot/arrow controls when there is more than one slide.
  slides?: HeroSlide[]
  autoplay?: boolean             // default true
  autoplayInterval?: number      // ms between slides; default 5000
  showDots?: boolean             // default true
  showArrows?: boolean           // default true
  contentPosition?: HeroContentPosition   // text/CTA placement over the carousel
  scrim?: string                 // optional rgba overlay over slides for legibility
  /** Headline size in px on a wide screen; it scales down with the viewport
   *  (never below ~1.9rem on a phone). Default ≈ 2.6rem. */
  titleSize?: number
  /** Headline weight (100–900). Default 800. */
  titleWeight?: number
}
export interface CtaBannerBlock extends BaseBlock {
  type: 'cta_banner'
  eyebrow?: string
  title: string
  subtitle?: string
  primaryCta?: ButtonSpec
  secondaryCta?: ButtonSpec
  align: Align
  background: Background
  textColor?: string
}
export interface SpacerBlock extends BaseBlock {
  type: 'spacer'
  height: number                 // px
}
export interface ColumnContent {
  id: string
  text?: string                  // legacy plain text; rendered when `blocks` is empty
  blocks?: Block[]               // v2 — child blocks rendered inside this column
  // Optional fixed column size: a CSS length like "180px" or "30%". When
  // omitted (or empty), the column FILLS the remaining space — all unsized
  // columns split what's left equally (CSS `1fr`). This lets a header be
  // e.g. logo "auto"/"220px" + nav (unsized → fills the rest), instead of
  // the default equal split. Numbers are treated as px.
  width?: string | number
}
export interface ColumnsBlock extends BaseBlock {
  type: 'columns'
  count: ColumnsCount
  columns: ColumnContent[]
  gap?: number
  /** 'stretch' = every column is as tall as the row, and its blocks fill it
   *  (a full-height panel beside a taller form). */
  align?: 'top' | 'center' | 'bottom' | 'stretch'
  // Horizontal placement of the columns within the row when their widths
  // don't fill it (e.g. all fixed-px). Maps to CSS `justify-content`.
  // Default 'start'. Ignored once an unsized (fill) column is present,
  // since fill columns already consume the slack.
  justify?: 'start' | 'center' | 'end' | 'between'
  /**
   * Order of the columns once they stack on a small screen. 'reverse' puts
   * the LAST column first — an image-left | text-right row reads text-first
   * on a phone. Default 'normal'.
   */
  mobileOrder?: 'normal' | 'reverse'
  /**
   * Small screens: 'stack' (default) puts each column on its own row; 'wrap'
   * keeps them side by side at their natural width and wraps only when the
   * row runs out of room — for icon rows, logo lock-ups and header tool bars.
   */
  mobileLayout?: 'stack' | 'wrap'
}
export interface ContainerBlock extends BaseBlock {
  type: 'container'
  background: Background
  padding?: number
  rounded?: boolean
  /** Plain text body — used only when the container has no `children`
   *  (a container with children renders them instead). */
  text?: string
  textColor?: string
  align?: Align
  /** Minimum height: px, or 'screen' for the full viewport height. */
  minHeight?: number | 'screen'
  /**
   * Vertical placement of the children when the container is taller than
   * they are: 'between' pins the first to the top and the last to the bottom
   * (a split-screen panel: logo on top, quote at the foot). Default 'start'.
   */
  justify?: 'start' | 'center' | 'end' | 'between'
  /** Widest the container grows, px; it centres in what is left over. */
  maxWidth?: number
}
/** A small status pill on a card — "In stock", "Sold out", "Pre-order". */
export interface CardStatus {
  label: string
  tone?: 'neutral' | 'success' | 'warning' | 'error' | 'info'
}

export interface CardSpec {
  id: string
  title: string
  subtitle?: string
  body?: string
  imageUrl?: string
  /**
   * Alternative text for `imageUrl`. Defaults to `title` — a card grid used
   * to publish every image with `alt=""`, which on a catalogue page is most
   * of the images on the site. Set it to `""` deliberately for an image
   * that really is decorative.
   */
  imageAlt?: string
  /**
   * A second picture shown while the pointer is over the card — the
   * product's other angle, as storefronts do. Cross-fades in CSS only, so
   * it works on a published page with no script; touch screens keep the
   * first picture.
   */
  hoverImageUrl?: string
  /**
   * How `imageUrl` fills its frame. `cover` (default) crops to fill — right
   * for a photo; `contain` shows the WHOLE picture — right for a product
   * shot whose edges carry the item, a logo or its name. Overrides the
   * grid's `cardStyle.imageFit`.
   */
  imageFit?: 'cover' | 'contain'
  /**
   * The image frame's shape as a CSS aspect ratio — `'1/1'` square,
   * `'4/3'`, `'3/4'`, `'16/9'`. Set, it replaces the fixed
   * `cardStyle.imageHeight`, so the frame scales with the card's width.
   */
  imageAspect?: string
  badge?: string
  cta?: ButtonSpec
  /** Small caps label above the title (category, section, kicker). */
  eyebrow?: string
  /**
   * The card's headline figure, rendered large in a footer row beside
   * `status`. A price had nowhere to go before this, so it was jammed into
   * `subtitle` alongside the category — legible, but body-sized and
   * indistinguishable from prose.
   */
  price?: string
  /**
   * How to render `price`. Default `'text'` — verbatim, for a price the
   * author typed. A price BOUND to a data source arrives as a bare number
   * (`3200`), so a catalogue reading from its master needs `'currency'` to
   * show `₹3,200` rather than `3200`.
   */
  priceFormat?: 'text' | 'currency' | 'number'
  /** ISO 4217, for `priceFormat: 'currency'`. Default 'USD'. */
  priceCurrency?: string
  /** BCP 47, for the grouping (Indian grouping needs 'en-IN'). */
  priceLocale?: string
  /** Qualifier beside the price: "per unit", "/mo", "excl. GST". */
  priceNote?: string
  /** The "was" price (MRP / list price), drawn crossed out beside `price`
   *  in the same format. Hidden when it is not above `price`. */
  compareAtPrice?: string
  /** With `compareAtPrice`: also show the saving as "N% off". */
  showSavings?: boolean
  status?: CardStatus
  /** A second action beside `cta` — "Add to cart" next to "View". */
  secondaryCta?: ButtonSpec
  /**
   * A small mark above the title — a product logo, a brand badge, an icon.
   * Kept at its natural aspect (unlike `imageUrl`, which is a cover photo
   * cropped to the card's width). A cloned marketing site's product cards
   * are usually exactly this: logo, title, blurb, button.
   */
  logoUrl?: string
  /** Logo height in px; default 48. */
  logoHeight?: number
  /** This card's own surface colour (a pastel per product, say). */
  background?: string
  /** Text colour on this card — set with a dark `background`. */
  textColor?: string
  /** Content alignment inside the card; default 'left'. */
  align?: 'left' | 'center'
  /**
   * A heart on the card that saves it to the visitor's wishlist (shopping
   * list). The saved item is taken from the card's add-to-cart button when
   * it has one (same code, name, price, picture), otherwise from the card.
   */
  wishlist?: boolean
  /** The wishlist item's code when the card has no add-to-cart button.
   *  Supports `{{alias.COL}}`. Default: the card's title. */
  wishlistSku?: string
}

/**
 * Grid-wide card styling. Cards used to be one fixed look (12px radius,
 * 16px padding, 1.05rem title), so a site rebuilt from a source whose cards
 * are big rounded panels with large titles came out as small generic tiles.
 */
export interface CardStyle {
  radius?: number                // px
  padding?: number               // px
  titleSize?: number             // px
  bodySize?: number              // px
  border?: boolean               // default true
  shadow?: ShadowPreset
  imageHeight?: number           // px, cover image height; default 160
  /** Grid-wide default for `CardSpec.imageFit`. */
  imageFit?: 'cover' | 'contain'
  /** Grid-wide default for `CardSpec.imageAspect` ('1/1', '4/3', …). */
  imageAspect?: string
}
export interface CardBlock extends BaseBlock {
  type: 'card'
  card: CardSpec
  /** The same styling `card_grid` takes — radius, padding, sizes, border,
   *  shadow, image frame — so a card REPEATED over rows can match one. */
  cardStyle?: CardStyle
  /**
   * Fill the container instead of the 360px cap a standalone card has. A
   * card repeated in a grid always fills its cell (equal heights, prices
   * lined up along the bottom); this opts a standalone card in too.
   */
  fill?: boolean
}
export interface CardGridBlock extends BaseBlock {
  type: 'card_grid'
  cards: CardSpec[]
  columns: ColumnsCount
  gap?: number
  cardStyle?: CardStyle
}

// ── Content sections ──
export interface FeatureItem {
  id: string
  icon?: string                  // emoji or short text — lightweight
  title: string
  description?: string
}
export interface FeatureGridBlock extends BaseBlock {
  type: 'feature_grid'
  eyebrow?: string
  heading?: string
  subheading?: string
  items: FeatureItem[]
  columns: ColumnsCount
  align?: Align
  iconStyle?: 'circle' | 'square' | 'plain'
}
export interface StatItem {
  id: string
  value: string
  suffix?: string
  label: string
  description?: string
}
export interface StatsBlock extends BaseBlock {
  type: 'stats'
  heading?: string
  items: StatItem[]
  columns: ColumnsCount
  align?: Align
  background?: Background
  // Animate each numeric value from 0 up to its final figure when the block
  // scrolls into view, driven by the SAME IntersectionObserver the block
  // reveal uses. Non-numeric values (and reduced-motion visitors) render
  // their final text immediately, so the number is never missing.
  countUp?: boolean
  countUpDuration?: number       // ms; default 1600
}
// A live countdown to a target instant. Unlike `stats` (static numbers),
// the days/hours/minutes/seconds tick in real time against the viewer's
// clock. `target` is an absolute ISO 8601 datetime; include a timezone
// offset (e.g. "2026-09-12T19:30:00+08:00") so every viewer counts down
// to the same instant regardless of their local zone.
export interface CountdownLabels {
  days?: string
  hours?: string
  minutes?: string
  seconds?: string
}
export interface CountdownBlock extends BaseBlock {
  type: 'countdown'
  heading?: string
  target: string                 // ISO 8601 datetime to count down to
  align?: Align
  columns?: ColumnsCount         // default 4 (3 when showSeconds is false)
  background?: Background
  showSeconds?: boolean          // default true
  labels?: CountdownLabels       // unit captions; default Days/Hours/Minutes/Seconds
  expiredText?: string           // shown once the target has passed
}

/**
 * A live clock: time ELAPSED since `from` (a kitchen ticket's age, a call's
 * hold time, a guest's wait). Ticks every second on every live surface; the
 * static site renders the value at render time. Every field accepts `{{…}}`
 * tokens, so inside a `repeat` it reads the row (`{{line.FIRED_AT}}`) and the
 * thresholds come from DATA (`{{line.TARGET_MIN}}`), never the page.
 */
export interface TimerBlock extends BaseBlock {
  type: 'timer'
  from: string                   // start (date/time value or token)
  until?: string                 // when set and non-empty, the clock stops here
  warnAfter?: string             // minutes before the amber state (token ok)
  dangerAfter?: string           // minutes before the red state (token ok)
  format?: 'auto' | 'mm:ss' | 'h:mm'   // auto = m:ss under an hour, then h:mm
  label?: string                 // small caption before the time
  textSize?: 'sm' | 'md' | 'lg'
  align?: Align
}
// One quote in a testimonial block. The single-quote fields on
// `TestimonialBlock` are the legacy shape: when `items` is absent the block
// renders the top-level quote/author, so pages saved before multi-item
// support keep working untouched.
export interface TestimonialItem {
  id: string
  quote: string
  authorName: string
  authorRole?: string
  authorAvatarUrl?: string
  companyLogoUrl?: string
  rating?: number                // 0-5
}

export interface TestimonialBlock extends BaseBlock {
  type: 'testimonial'
  // Legacy single-quote fields (still authoritative when `items` is empty).
  quote: string
  authorName: string
  authorRole?: string
  authorAvatarUrl?: string
  companyLogoUrl?: string
  rating?: number                // 0-5
  align?: Align
  // ── Multi-item (Tier 6.1) ──
  items?: TestimonialItem[]
  //   'single'   — one quote (the legacy look; the default).
  //   'carousel' — auto-advancing slider reusing the hero carousel script.
  //   'wall'     — a responsive grid of every quote at once.
  mode?: 'single' | 'carousel' | 'wall'
  columns?: ColumnsCount         // wall mode; default 3
  autoplay?: boolean             // carousel; default true
  autoplayInterval?: number      // ms; default 6000
}
export interface PricingTier {
  id: string
  name: string
  price: string                  // raw, e.g. "$49" — the FIRST period's price
  period?: string                // "/mo"
  description?: string
  features: string[]
  cta?: ButtonSpec
  highlighted?: boolean
  badge?: string
  // Alternate-period price, shown when the author enables the period toggle
  // and the visitor switches to the second period (e.g. yearly).
  altPrice?: string
  altPeriod?: string
}
export interface PricingBlock extends BaseBlock {
  type: 'pricing'
  heading?: string
  subheading?: string
  tiers: PricingTier[]
  columns: ColumnsCount
  // ── Period toggle (Tier 6.2) ──
  // When true, a monthly/yearly switch renders above the tiers and swaps
  // each tier between `price/period` and `altPrice/altPeriod`. Tiers with no
  // alt price keep their single price on both settings.
  showPeriodToggle?: boolean
  periodLabel?: string           // default 'Monthly'
  altPeriodLabel?: string        // default 'Yearly'
  altPeriodBadge?: string        // e.g. 'Save 20%'
  defaultPeriod?: 'primary' | 'alt'
}
export interface FaqItem {
  id: string
  question: string
  answer: string
}
export interface FaqBlock extends BaseBlock {
  type: 'faq'
  heading?: string
  items: FaqItem[]
}
export interface TabItem {
  id: string
  label: string
  // Legacy plain-text body. Rendered when `blocks` is empty/absent so older
  // pages keep working; the first edit of an empty body promotes to a
  // single-paragraph child block.
  body?: string
  // v2 — child blocks rendered inside this tab. Can be any block type,
  // including another container, so nesting is unbounded.
  blocks?: Block[]
}
export interface TabsBlock extends BaseBlock {
  type: 'tabs'
  items: TabItem[]
  variant?: 'underline' | 'pill'
  overflow?: 'wrap' | 'scroll'
}
export interface AccordionItem {
  id: string
  title: string
  body?: string                    // legacy; rendered when `blocks` is empty
  blocks?: Block[]                 // v2 — child blocks
  defaultOpen?: boolean
}
export interface AccordionBlock extends BaseBlock {
  type: 'accordion'
  items: AccordionItem[]
}
export type CalloutVariant = 'info' | 'success' | 'warning' | 'error' | 'tip'
// Where the icon sits relative to the title+body block. Defaults to
// 'left' (icon column on the left, content takes the rest). 'top'
// stacks the icon above the content (centered when align='center').
// 'none' hides the icon entirely.
export type CalloutIconPosition = 'left' | 'top' | 'none'
export type CalloutIconSize = 'sm' | 'md' | 'lg'
export interface CalloutBlock extends BaseBlock {
  type: 'callout'
  variant: CalloutVariant
  title?: string
  body: string
  icon?: string
  // ── Layout / placement (all optional; default left + md + left text) ──
  iconPosition?: CalloutIconPosition   // default 'left'
  iconSize?: CalloutIconSize           // default 'md' (1.25rem)
  align?: Align                        // text alignment within content; default 'left'
  padding?: number                     // px; default 14
  gap?: number                         // px between icon and content; default 12
}

// ── Timeline ──
// Vertical event timeline with markers on a connecting axis. Items can be
// laid out single-side ('vertical') or zig-zagging ('alternating', which
// stacks to single-side under 720px). Markers may be filled dots, emoji
// icons, or auto-numbered chips.
export type TimelineLayout = 'vertical' | 'alternating'
export type TimelineMarkerStyle = 'dot' | 'icon' | 'number'
export interface TimelineItem {
  id: string
  date?: string                  // free-form date label, e.g. '2024 Q1'
  title: string
  description?: string
  icon?: string                  // emoji / short text — rendered when markerStyle='icon'
  badge?: string                 // small status pill, e.g. 'Done'
  accent?: string                // optional per-item color override
}
export interface TimelineBlock extends BaseBlock {
  type: 'timeline'
  eyebrow?: string
  heading?: string
  subheading?: string
  items: TimelineItem[]
  layout?: TimelineLayout        // default 'vertical'
  markerStyle?: TimelineMarkerStyle  // default 'dot'
  align?: Align                  // header alignment
  accentColor?: string           // axis/marker color (defaults to --pg-primary)
}

// ── Action ──
export interface ButtonBlock extends BaseBlock {
  type: 'button'
  button: ButtonSpec
  align: Align
}
export interface ButtonGroupBlock extends BaseBlock {
  type: 'button_group'
  buttons: ButtonSpec[]
  align: Align
  gap?: number
}

// ── Form ──
export interface NewsletterBlock extends BaseBlock {
  type: 'newsletter'
  heading?: string
  subheading?: string
  placeholder?: string
  buttonLabel?: string
  background?: Background
  align?: Align
  // PageAction.name dispatched on submit. The entered address is exposed as
  // the `email` page variable so the action's call_api body can read it. When
  // unset the submission falls through to the built-in submissions store
  // (see FormDestination) rather than being silently dropped.
  submitAction?: string
  destination?: FormDestination
  notifyEmail?: string
  record?: FormRecordTarget      // destination 'transaction'
  spam?: FormSpamGuard
  success?: FormSuccess
}
export interface ContactFormField {
  id: string
  name: string
  label: string
  type: 'text' | 'email' | 'tel' | 'textarea'
  placeholder?: string
  required?: boolean
}
export interface ContactFormBlock extends BaseBlock {
  type: 'contact_form'
  heading?: string
  subheading?: string
  fields: ContactFormField[]
  buttonLabel: string
  align?: Align
  // PageAction.name dispatched on submit. Each field value is exposed as a
  // page variable named after the field's `name` so the action's call_api
  // body can read them. Unset falls through to the built-in submissions
  // store (see FormDestination).
  submitAction?: string
  destination?: FormDestination
  notifyEmail?: string
  record?: FormRecordTarget      // destination 'transaction'
  spam?: FormSpamGuard
  success?: FormSuccess
}

// ── App-integration blocks ──
export interface InputGroupBlock extends BaseBlock {
  type: 'input_group'
  inputs: string[]               // list of PageInput.name
  layout?: 'inline' | 'stacked'
}
export interface EmbedBlock extends BaseBlock {
  type: 'embed'
  embed: EmbedConfig
  /** Wording of the card shown INSTEAD of the embed when its data needs a
   *  signed-in account (SIGN_IN_REQUIRED / SESSION_EXPIRED /
   *  PROJECT_USER_REQUIRED). Every field optional; see page-runtime
   *  `signInPrompt.tsx` for the defaults. */
  signInPrompt?: { title?: string; body?: string; signInLabel?: string; registerLabel?: string }
}

// ── File upload block ──
// A reusable file/image attachment field for smart pages, backed by the same
// document store as the transaction image/document widgets. The author picks a
// DOCUMENT TYPE and a TRANSACTION ID — each a fixed value or a page-level
// variable (FilterValueRef) — which together determine the storage folder
// (`<doc_type>/<transaction_id>/`), giving an archivable, per-record layout.
// The uploaded file's served URL is written into `valueVar` (a page input) so
// the rest of the page can reference it.
export type FileUploadPreviewMode = 'auto' | 'image' | 'pdf' | 'text' | 'none'

export interface FileUploadBlock extends BaseBlock {
  type: 'file_upload'
  docType: FilterValueRef          // document type: fixed or page input
  transactionId: FilterValueRef    // transaction id the file belongs to
  kind?: 'image' | 'document'      // widget style + accept default (default 'document')
  accept?: string                  // overrides the default accept filter
  valueVar?: string                // page input that holds/seeds the uploaded URL
  label?: string                   // caption shown above the control
  // Inline preview of the uploaded file. 'auto' picks image/pdf/text from the
  // file type; 'none' shows only the file chip (today's behavior). Default 'auto'.
  previewMode?: FileUploadPreviewMode
  // Name of a PageAction (page.actions[].name) run after a successful upload —
  // e.g. a call_api step that posts the uploaded URL to an extraction service
  // and writes the returned JSON into a page variable (save_response_var).
  onUploadAction?: string
  // Server-minted, page-bound token (external pages only) — see EmbedConfig.
  _embedToken?: string
}

// ── eCommerce blocks (Proteus Vision integration) ──
//
// Each block carries its own data-source config (API endpoint or — for
// EntityPicker — a transaction object) plus an `ArgMapping`-based
// binding surface for inputs/outputs, mirroring the transaction-embed
// pattern. See livepreview/bindingResolver.ts for the input vs output
// convention. The built-in endpoint registry lives in
// livepreview/ecommerce/endpoints.ts.

// Identifier for a built-in ecommerce API endpoint. 'custom' means the
// authored URL on the block is used instead. The runtime resolves the
// id against ECOM_ENDPOINTS in livepreview/ecommerce/endpoints.ts.
export type EcomEndpointId =
  | 'customers'
  | 'products'
  | 'sales_orders'
  | 'custom'

export interface EcomSourceApi {
  kind: 'api'
  endpointId: EcomEndpointId
  customUrl?: string                  // only when endpointId === 'custom'
  // Page/static values bound to the request: query params for GET
  // endpoints, body fields for POST endpoints. Each row resolved via
  // resolveArgsAsRecord at request time.
  args?: ArgMapping[]
}

export interface EcomSourceTransaction {
  kind: 'transaction'
  obj_name: string
  filters?: EmbedFilter[]             // list-mode filter rows (same shape as EmbedConfig.filters)
  args?: ArgMapping[]                 // add/edit pre-fills (same shape as EmbedConfig.args)
}

export type EcomSource = EcomSourceApi | EcomSourceTransaction

// Generic entity picker (formerly CustomerPicker). Two source modes:
//   - API: built-in default (customers) or any list endpoint
//   - Transaction: picks rows from a Vision transaction object, just
//     like the embed block's list+selection flow
// On selection the picked record is written to the destination vars
// configured in `outputBindings`.
export interface EntityPickerBlock extends BaseBlock {
  type: 'entity_picker'
  source: EcomSource
  outputBindings: ArgMapping[]        // target='value' writes whole record; 'id' writes record.id; etc.
  toggleBinding?: ArgMapping          // optional boolean var flipped on "+ New" click
  showCreateNewToggle?: boolean       // default true
  searchPlaceholder?: string
  layout?: 'compact' | 'spacious'
}

export interface NewCustomerFormBlock extends BaseBlock {
  type: 'new_customer_form'
  source: EcomSourceApi               // default endpointId='customers'; custom URL falls back to raw JSON body editor
  outputBindings: ArgMapping[]
  toggleBinding?: ArgMapping
  layout?: 'single-column' | 'two-column'
  submitLabel?: string                // default "Save customer"
}

// Filters configured on the catalogue modal. When omitted the modal
// derives filters from product metadata heuristically.
export interface ProductCatalogueFilter {
  key: string
  label: string
  type: 'range' | 'multi-select' | 'toggle'
  sourceField?: string                // metadata key on the product (default: same as key)
  options?: { value: string; label: string }[]
}

export interface ProductSelectionBlock extends BaseBlock {
  type: 'product_selection'
  source: EcomSource                  // default: { kind:'api', endpointId:'products' }
  cartBinding: ArgMapping             // where the cart array is written; outputVarName is also used for reads
  gateBinding?: ArgMapping            // optional read binding; when set + falsy, the picker shows a gate
  openInModalBinding?: ArgMapping     // optional boolean binding to programmatically open/close the catalogue modal
  // Inline mode
  layout?: 'grid' | 'list'
  columns?: 2 | 3 | 4
  allowPriceOverride?: boolean
  initialStep?: 'product' | 'style' | 'customize'
  showRecentlyViewed?: boolean
  imageField?: string                 // default 'imageUrl'
  // Top-level shape of the picker experience. See above.
  selectorMode?: 'wizard' | 'catalogue' | 'both'
  previewDomain?: 'none' | 'garment' | 'gallery'
  catalogueMode?: 'inline-only' | 'modal-only' | 'both'   // legacy; prefer selectorMode
  catalogueButtonLabel?: string
  catalogueTitle?: string
  catalogueColumns?: 3 | 4 | 5
  showCategorySidebar?: boolean
  filters?: ProductCatalogueFilter[]
  showCartDrawer?: boolean
}

export interface ProductPreviewBlock extends BaseBlock {
  type: 'product_preview'
  // Where the product / style / attributes come from.
  //   'product_selection_state' (default) — read live from the shared
  //     productSelectionStore singleton, so the preview updates the
  //     instant the assistant tweaks a swatch / pill / measurement on
  //     the product_selection block (or in the catalogue modal).
  //   'pageVar' — read from explicit page-variable bindings.
  attributesSource?: 'product_selection_state' | 'pageVar'
  productBinding?: ArgMapping                  // pageVar mode only
  styleBinding?: ArgMapping                    // pageVar mode only
  attributesBinding?: ArgMapping               // pageVar mode only — Record<string, string|number>
  layout?: 'card' | 'large' | 'inline'         // default 'card'
  aspect?: '1:1' | '4:3' | '3:4' | '16:9'      // default '1:1'
  showCaption?: boolean                        // default true
  showAttributeChips?: boolean                 // default true
  emptyStateTitle?: string                     // default "Pick a product to see a preview"
  emptyStateBody?: string
  placementMasterId?: string                   // design-time hint; ignored at runtime
}

export interface OrderSummaryBlock extends BaseBlock {
  type: 'order_summary'
  cartBinding: ArgMapping             // reads + writes the cart array
  editable?: boolean                  // default true; row edit/remove
  showSubtotal?: boolean              // default true
  showTax?: boolean
  showDiscount?: boolean
  emptyStateTitle?: string
  emptyStateBody?: string
}

// Per-document persisted state for OrderSubmit. When a doc is reopened
// after a successful submit, the success card is rehydrated from this.
export interface OrderSubmitCaptureState {
  result?: {
    orderId?: string
    orderNumber?: string
    status?: string
    totalAmount?: number
    currency?: string
    createdAt?: string
    [k: string]: unknown
  }
}

export interface OrderSubmitBlock extends BaseBlock {
  type: 'order_submit'
  source: EcomSourceApi               // default endpointId='sales_orders'
  customerBinding: ArgMapping         // reads the customer record from a page var
  cartBinding: ArgMapping             // reads the cart array
  outputBindings: ArgMapping[]        // typically [orderId, orderNumber]
  captureState?: OrderSubmitCaptureState
  buttonLabel?: string                // default "Place Order"
  successTitle?: string               // default "Order {{orderNumber}} created"
  successBody?: string
  showStartNew?: boolean              // default true; clears customer + cart on click
  onSuccessNavigateTo?: string        // optional URL after success
}

// ── Payment checkout ──
// Takes money for ONE record of this application — a sales order, a booking,
// an invoice — through the gateway its project manager connected.
//
// WHY EVERY BINDING IS A COLUMN NAME. This block's settings travel to the
// browser and come back with the checkout request, so anything holding a
// VALUE here would be a value a customer could edit. The server re-reads the
// named record and resolves the amount from `amountColumn` itself; the worst
// a tampered block can do is name a column that holds no amount, and the
// order is then refused.
/**
 * The application's own "connect your gateway" screen, as a block.
 *
 * The sibling `payment_checkout` SPENDS a gateway configuration; this one
 * CAPTURES it, so an application can carry its own payments settings page in
 * its navigation instead of sending its administrator into the builder. Both
 * read the same stored row — nothing is copied between them.
 *
 * IT CARRIES NO CREDENTIAL AND NO BINDING. Unlike every other block here the
 * settings are only presentation: which provider the form opens on, and what
 * to call it. The credentials themselves are written straight to the server,
 * encrypted there, and never returned — so there is nothing about this block
 * that a tampered page model could turn into a secret.
 *
 * WHO MAY USE IT is not the author's choice either: the endpoints behind it
 * take the project owner or the FUNC-ADMIN role on the application, and the
 * block renders a "not yours to change" note to anyone else.
 */
export interface PaymentSettingsBlock extends BaseBlock {
  type: 'payment_settings'
  /** Which provider the form opens on. Default 'razorpay'. */
  defaultProvider?: string
  heading?: string
  description?: string
}

export interface PaymentCheckoutBlock extends BaseBlock {
  type: 'payment_checkout'
  // WHICH record is being paid for. `objName` is a transaction object of this
  // application; `txnIdVar` is the page variable holding the saved record's
  // id — typically written by an earlier `order_submit` / `embed` block on
  // the same page.
  objName: string
  txnIdVar: string
  // The column holding what to charge, in major units. REQUIRED.
  amountColumn: string
  currency?: string
  currencyColumn?: string
  referenceColumn?: string
  // WHO MAY PAY. The column naming the customer the record belongs to; the
  // signed-in site account must match it. A public storefront MUST set this —
  // without it the block serves only signed-in users of the application.
  ownerColumn?: string
  payerNameColumn?: string
  payerEmailColumn?: string
  payerContactColumn?: string
  // What to stamp on the record once the money is captured.
  writeback?: {
    statusColumn?: string
    statusValue?: string
    /** Status after a CONFIRMED failed attempt (default "FAILED"); the order
     *  stays placed and can be paid again. Never replaces a paid / COD value. */
    failedValue?: string
    paymentIdColumn?: string
    paidAmountColumn?: string
    paidAtColumn?: string
    methodColumn?: string
  }
  // Presentation.
  buttonLabel?: string                 // default "Pay now"
  heading?: string
  description?: string
  successTitle?: string                // default "Payment received"
  successBody?: string
  /** Where to go once paid / COD is chosen; `{{txn}}` = the record's id
   *  (e.g. an order-confirmation page: `/order?order={{txn}}`). */
  onSuccessNavigateTo?: string
  /** Read the record id from this URL parameter when `txnIdVar` is empty —
   *  a "pay for order" page opened from the customer's order list. */
  txnIdFromQuery?: string
  /** "Continue shopping" link offered beside a failed / finished payment. */
  continueHref?: string
  continueLabel?: string
  // Offer cash on delivery beside the gateway, when the project allows it.
  // The button records the CHOICE on the record through the payment rail
  // (POST /api/app-payments/{owner}/{project}/cod) and takes no money. The
  // value stamped is NOT set here: the server finds the object's payment
  // ACTION whose write-back status column is `writeback.statusColumn` and
  // stamps that action's `payment.cod_status_value` from the MODEL — a value
  // in this block would be a value a customer could edit.
  showCodOption?: boolean
  codLabel?: string                    // default "Cash on delivery"
  /** Shown when the customer picks cash on delivery, before they confirm it
   *  (packing / unboxing instructions). Blank lines separate paragraphs. */
  codNote?: string
  /** Links under the pay buttons (terms, refund, privacy), with a lead-in. */
  policyLinks?: { label: string; href: string }[]
  policyText?: string                  // default "By placing your order you agree to our"
  /** @deprecated Ignored — the model's `payment.cod_status_value` decides. */
  codStatusValue?: string
  /**
   * Render nothing until `txnIdVar` holds a record — a checkout whose payment
   * step appears once the order is placed, instead of a disabled card saying
   * there is nothing to pay for yet. Default false.
   */
  hideUntilRecord?: boolean
}

// ════════════════════════════════════════════════════════════════════
// Tier 6 — marketing block types
// --------------------------------------------------------------------
// Each is presentational and additive: one shared renderer plus thin
// per-surface pass-throughs. None require live data or an API.
// ════════════════════════════════════════════════════════════════════

// ── Bento grid ──
// The current default layout idiom for product pages: a grid of unequally
// sized tiles. `span`/`rowSpan` place each cell on a 4-column (desktop)
// grid that collapses to 2 columns on tablet and 1 on mobile.
export interface BentoCell {
  id: string
  title?: string
  body?: string
  eyebrow?: string
  icon?: string
  imageUrl?: string
  cta?: ButtonSpec
  span?: 1 | 2 | 3 | 4           // column span, default 1
  rowSpan?: 1 | 2                // default 1
  background?: Background
  align?: Align
}
export interface BentoGridBlock extends BaseBlock {
  type: 'bento_grid'
  eyebrow?: string
  heading?: string
  subheading?: string
  cells: BentoCell[]
  gap?: number                   // px, default 16
  columns?: 3 | 4                // desktop track count, default 4
}

// ── Marquee ──
// A continuously scrolling strip of text and/or logos. Pure CSS keyframes,
// so it runs on the static site; suppressed under `prefers-reduced-motion`.
export interface MarqueeItem {
  id: string
  text?: string
  imageUrl?: string
  href?: string
}
export interface MarqueeBlock extends BaseBlock {
  type: 'marquee'
  items: MarqueeItem[]
  speed?: number                 // seconds per pass; default 30
  direction?: 'left' | 'right'
  pauseOnHover?: boolean         // default true
  separator?: string             // glyph drawn between text items, e.g. '•'
  fadeEdges?: boolean            // mask the strip's left/right edges (default true)
  itemHeight?: number            // px for image items; default 32
}

// ── Counter ──
// Standalone animated count-up figures (distinct from `stats`, which is a
// static figure grid that can OPT IN to count-up). Driven by the shared
// reveal observer; renders its final value immediately under reduced motion.
export interface CounterItem {
  id: string
  value: number
  prefix?: string
  suffix?: string
  label: string
  decimals?: number
}
export interface CounterBlock extends BaseBlock {
  type: 'counter'
  heading?: string
  items: CounterItem[]
  columns?: ColumnsCount
  align?: Align
  duration?: number              // ms; default 1600
  separator?: boolean            // thousands separators; default true
}

// ── Before / after image comparison ──
// A draggable divider wipes between two images. The static site gets a CSS
// `input[type=range]` slider (works with no JS); interactive surfaces add
// pointer dragging and keyboard control.
export interface BeforeAfterBlock extends BaseBlock {
  type: 'before_after'
  beforeUrl: string
  afterUrl: string
  beforeLabel?: string
  afterLabel?: string
  caption?: string
  aspect?: MediaAspect           // default '16:9'
  initial?: number               // starting divider position, 0..100; default 50
  orientation?: 'horizontal' | 'vertical'
}

// ── General-purpose carousel ──
// Until now only `hero` could hold slides. This is the same mechanism as a
// standalone block, so any content can be a slider.
export interface CarouselSlide {
  id: string
  imageUrl?: string
  title?: string
  body?: string
  cta?: ButtonSpec
  background?: Background
}
export interface CarouselBlock extends BaseBlock {
  type: 'carousel'
  slides: CarouselSlide[]
  height?: number                // px; default 380
  autoplay?: boolean             // default true
  autoplayInterval?: number      // ms; default 5000
  showDots?: boolean             // default true
  showArrows?: boolean           // default true
  /** Show more than one slide at a time (a "cards" carousel). Default 1. */
  perView?: 1 | 2 | 3
}

// ── Team / bio ──
export interface TeamMember {
  id: string
  name: string
  role?: string
  bio?: string
  photoUrl?: string
  links?: SocialLink[]
}
export interface TeamBlock extends BaseBlock {
  type: 'team'
  eyebrow?: string
  heading?: string
  subheading?: string
  members: TeamMember[]
  columns?: ColumnsCount
  shape?: 'circle' | 'rounded' | 'square'
  align?: Align
}

// ── Step / process ──
export interface ProcessStep {
  id: string
  title: string
  description?: string
  icon?: string
}
export interface StepsBlock extends BaseBlock {
  type: 'steps'
  eyebrow?: string
  heading?: string
  subheading?: string
  steps: ProcessStep[]
  layout?: 'horizontal' | 'vertical'
  numbered?: boolean             // default true
  align?: Align
  /**
   * `progress` = one compact line for a multi-step flow ("Cart ✓ — Details —
   * Payment"): steps before `current` are done (✓), `current` is emphasised,
   * the rest muted. Default `cards` (the numbered process explainer).
   */
  variant?: 'cards' | 'progress'
  /** 0-based index of the step the visitor is on (`progress`). Default 0. */
  current?: number
  /**
   * Move `current` on as the page progresses: the LAST rule whose page
   * variable holds a value wins (e.g. a checkout's `orderId` → Payment).
   */
  advance?: { whenVar: string; current: number }[]
}

// ── Comparison table ──
// A feature matrix — the "us vs them" / plan-comparison section that is
// today hand-built out of `card_grid`. A cell value of `true`/`false`
// renders a check/cross glyph; anything else renders as text.
export interface ComparisonRow {
  id: string
  label: string
  /** One entry per column, in column order. */
  values: (string | boolean)[]
  group?: string                 // optional row-group heading
}
export interface ComparisonColumn {
  id: string
  label: string
  highlighted?: boolean
  badge?: string
}
export interface ComparisonTableBlock extends BaseBlock {
  type: 'comparison_table'
  heading?: string
  subheading?: string
  columns: ComparisonColumn[]
  rows: ComparisonRow[]
  stickyHeader?: boolean         // default true
}

// ── Social links / share ──
export type SocialNetwork =
  | 'x' | 'facebook' | 'instagram' | 'linkedin' | 'youtube' | 'github'
  | 'tiktok' | 'threads' | 'discord' | 'whatsapp' | 'telegram'
  | 'mastodon' | 'pinterest' | 'reddit' | 'email' | 'website' | 'rss'

export interface SocialLink {
  id: string
  network: SocialNetwork
  url: string
  label?: string
}
export interface SocialLinksBlock extends BaseBlock {
  type: 'social_links'
  //   'follow' — the author's own profiles (`links`).
  //   'share'  — share THIS page to the listed networks (urls are built at
  //              render time from the page URL, so no `links` are authored).
  mode?: 'follow' | 'share'
  links?: SocialLink[]
  networks?: SocialNetwork[]     // share mode: which buttons to offer
  align?: Align
  // NOT `size` — that name belongs to BaseBlock.size (the width/height box).
  iconSize?: number              // px glyph; default 20
  variant?: 'plain' | 'circle' | 'square'
  shareTitle?: string            // share mode: text seeded into the share intent
}

// ── Lottie / animated SVG ──
export interface LottieBlock extends BaseBlock {
  type: 'lottie'
  url: string                    // .json (lottie) or .svg
  height?: number                // px; default 280
  loop?: boolean                 // default true
  autoplay?: boolean             // default true
  //   'scroll' plays the animation only once it enters the viewport (via the
  //   shared reveal observer) rather than on load.
  trigger?: 'load' | 'scroll' | 'hover'
  /** Static image shown on SSR, under reduced motion, and while loading. */
  posterUrl?: string
  caption?: string
}

// ── Cookie-consent banner ──
// Page/site-level notice. `categories` drives a preferences dialog; when
// omitted the banner is accept/decline only. The visitor's choice is stored
// in localStorage under `key` and is readable by analytics gating.
export interface ConsentCategory {
  id: string
  label: string
  description?: string
  required?: boolean             // always-on (strictly necessary)
  defaultOn?: boolean
}
export interface CookieConsentBlock extends BaseBlock {
  type: 'cookie_consent'
  message: string
  acceptLabel?: string           // default 'Accept'
  declineLabel?: string          // default 'Decline'
  settingsLabel?: string         // default 'Preferences'
  policyHref?: string
  policyLabel?: string           // default 'Privacy policy'
  position?: 'bottom' | 'top' | 'bottom-left' | 'bottom-right'
  categories?: ConsentCategory[]
  storageKey?: string            // default 'pg_consent'
}

/**
 * A call-to-action pinned to the viewport — the "Book Demo" tab on the edge
 * of a marketing site, a chat bubble in a corner. Put it on the site HEADER
 * chrome page and it shows on every page. `vertical` turns an edge tab on its
 * side (reads bottom-to-top on the right edge, top-to-bottom on the left).
 */
export interface FloatingButtonBlock extends BaseBlock {
  type: 'floating_button'
  button: ButtonSpec
  position?: 'right-center' | 'left-center' | 'bottom-right' | 'bottom-left' | 'top-right'
  vertical?: boolean
  background?: string            // default: the theme primary
  textColor?: string             // default: on-primary
  /** Hide below this viewport width (px) — a side tab crowds a phone. */
  hideBelow?: number
  /** Below this viewport width (px) show just the icon in a round button —
   *  the label stays for screen readers. Needs `button.icon`. */
  iconOnlyBelow?: number
}

export type Block =
  | HeadingBlock
  | ParagraphBlock
  | BulletListBlock
  | NumberedListBlock
  | TodoBlock
  | QuoteBlock
  | CodeBlock
  | DividerBlock
  | ImageBlock
  | VideoBlock
  | GalleryBlock
  | LogoCloudBlock
  | MapBlock
  | HeroBlock
  | CtaBannerBlock
  | SpacerBlock
  | ColumnsBlock
  | ContainerBlock
  | CardBlock
  | CardGridBlock
  | FeatureGridBlock
  | StatsBlock
  | CountdownBlock
  | TimerBlock
  | TestimonialBlock
  | PricingBlock
  | FaqBlock
  | TabsBlock
  | AccordionBlock
  | CalloutBlock
  | TimelineBlock
  | ButtonBlock
  | ButtonGroupBlock
  | NewsletterBlock
  | ContactFormBlock
  | InputGroupBlock
  | EmbedBlock
  | FileUploadBlock
  | FormBlock
  | CustomCodeBlock
  | CustomHtmlBlock
  | NavMenuBlock
  | BreadcrumbBlock
  | FilterBarBlock
  | CartBadgeBlock
  | CartLinesBlock
  | CartSummaryBlock
  | WishlistButtonBlock
  | WishlistBadgeBlock
  | WishlistLinesBlock
  | TableBlock
  | VariableBlock
  | NotepadBlock
  | RichContentBlock
  | CollectionSearchBlock
  | CommentsBlock
  | EntityPickerBlock
  | NewCustomerFormBlock
  | ProductSelectionBlock
  | ProductPreviewBlock
  | OrderSummaryBlock
  | OrderSubmitBlock
  | PaymentCheckoutBlock
  | PaymentSettingsBlock
  | CartCheckoutBlock
  | MyRecordsBlock
  | SavedRecordsBlock
  | ReviewsBlock
  | ChatbotBlock
  | AuthFormBlock
  | AuthMenuBlock
  | BentoGridBlock
  | MarqueeBlock
  | CounterBlock
  | BeforeAfterBlock
  | CarouselBlock
  | TeamBlock
  | StepsBlock
  | ComparisonTableBlock
  | SocialLinksBlock
  | LottieBlock
  | CookieConsentBlock
  | FloatingButtonBlock

// ─── Page theme (palette + typography + layout) ───────────
// These travel with the page model and are applied as CSS variables and
// wrapper styles by the canvas. Every field is optional so existing pages
// keep rendering with the defaults; the theme editor fills them in as the
// author tweaks settings.

// ─── Palette: full information-hierarchy token set ────────
// Sparse on disk — every field is optional, and `resolveTheme()` derives
// missing tokens from the explicit ones (e.g. `onPrimary` is computed
// from `primary`'s perceived luminance when not set, `heading` falls
// back to `text`, `surfaceAlt` is derived from `surface`/`background`,
// etc.). This means existing pages with only `{primary, accent, text,
// textSecondary, background, surface, border}` keep rendering — the
// derivation fills the rest with WCAG-AA-friendly values.
//
// Token groups:
//   brand    — the user's brand colors and their guaranteed-readable
//              text-on-color counterparts.
//   text     — typographic hierarchy. `heading` for h1/h2/h3, `body` for
//              paragraphs, `muted` for secondary captions, `subtle` for
//              the quietest helper text, `link` for inline links. The
//              `onSurface` / `onSurfaceAlt` variants exist for the rare
//              case a surface needs its own text color (e.g. dark card on
//              light page); when not set they fall back to `body`.
//   surface  — page chrome from back to front: `background` (page),
//              `surface` (cards / containers), `surfaceAlt` (a slightly
//              different elevated surface — e.g. striped table row),
//              `surfaceMuted` (code blocks, disabled inputs), `elevated`
//              (modals / popovers). `border` is the standard divider;
//              `borderStrong` is for emphasized outlines.
//   state    — semantic colors used by callouts, badges, validation
//              messages. The `*Bg` variants are the soft tinted
//              backgrounds; the base color is the icon/border/text color.
export interface PalettePreset {
  // ── Brand ──
  primary?: string
  primaryHover?: string
  onPrimary?: string             // text/icon color that sits on primary
  accent?: string
  accentHover?: string
  onAccent?: string

  // ── Text hierarchy ──
  heading?: string               // h1/h2/h3 color (defaults to text)
  text?: string                  // body text (the canonical typographic color)
  muted?: string                 // captions, secondary descriptions
  subtle?: string                // quietest helper text (placeholders, footnotes)
  link?: string
  linkHover?: string
  onSurface?: string             // text color on `surface`
  onSurfaceAlt?: string          // text color on `surfaceAlt`
  inverted?: string              // text color on a dark/colored band (heroes)

  // ── Back-compat alias ──
  textSecondary?: string         // legacy alias for `muted`; resolveTheme reads either

  // ── Surface stack ──
  background?: string            // page background
  surface?: string               // cards / containers
  surfaceAlt?: string            // alternate surface (striped rows, elevated cards)
  surfaceMuted?: string          // inputs / code blocks
  elevated?: string              // modals / popovers
  border?: string                // standard divider
  borderStrong?: string          // emphasized outline

  // ── Semantic state colors ──
  info?: string
  infoBg?: string
  success?: string
  successBg?: string
  warning?: string
  warningBg?: string
  error?: string
  errorBg?: string
}

export type FontFamilyPreset =
  | 'system' | 'sans' | 'serif' | 'mono'
  | 'inter' | 'roboto' | 'poppins' | 'playfair' | 'merriweather'

export interface TypographyPreset {
  fontFamily?: FontFamilyPreset
  headingFamily?: FontFamilyPreset
  baseSize?: number              // px, e.g. 16
  scaleRatio?: number            // typographic scale, e.g. 1.25
  lineHeight?: number            // unitless body line-height, e.g. 1.6
  fontWeight?: number            // body weight: 300 / 400 / 500 / 600 / 700
  // ── Heading detail (drive `--pg-h1/2/3` + heading weight/line-height) ──
  // The heading scale is `baseSize × scaleRatio^n` (h3=¹, h2=², h1=³), emitted
  // as `--pg-h1/2/3` and consumed by every heading renderer (design canvas,
  // in-app preview/run, deployed runtime, SSR) so the theme actually controls
  // heading sizes — they were previously hardcoded per surface and drifted.
  fluid?: boolean                // headings scale fluidly with viewport (clamp())
  headingWeight?: number         // heading font-weight (default 700)
  headingLineHeight?: number     // heading line-height (default 1.2, tighter than body)
  headingLetterSpacing?: number  // heading tracking in em (default 0; e.g. -0.02)
  // ── Arbitrary Google families (Tier 5C) ──
  // The 9-preset enum above is the curated set. These let an author pick ANY
  // Google family without a schema change: the name is resolved against the
  // shared font registry in @twasta/page-runtime/theme, which is the single
  // source every surface (SSR <link>, designer index.html, runtime
  // index.html) builds its webfont URL from.
  customFontFamily?: string      // e.g. 'Space Grotesk' — overrides fontFamily
  customHeadingFamily?: string   // e.g. 'Fraunces'      — overrides headingFamily
  customFontWeights?: number[]   // weights to load; default [400,500,600,700]
  // ── Eyebrow / overline token ──
  // The small-caps label above a hero or section heading. Every block used
  // to hardcode its own; these drive `--pg-eyebrow-*` so they are consistent.
  eyebrowSize?: number           // px; default 12
  eyebrowWeight?: number         // default 700
  eyebrowTracking?: number       // em; default 0.08
  eyebrowTransform?: 'uppercase' | 'none'   // default 'uppercase'
  /** Default measure (max line length) for paragraph-carrying blocks, px. */
  bodyMaxWidth?: number
}

export type ContentWidthPreset = 'narrow' | 'normal' | 'wide' | 'full'

export interface PageLayoutPreset {
  contentWidth?: ContentWidthPreset
  maxWidth?: number              // px override (when contentWidth = custom-ish)
  paddingX?: number              // px horizontal canvas padding
  paddingY?: number              // px vertical canvas padding
  background?: Background        // page-wide background (color/gradient/image)
  align?: Align                  // default text alignment for block wrappers
  blockGap?: number              // px between blocks
  borderRadius?: number          // global wrapper rounding (cards, inputs)
  buttonRadius?: number          // px rounding of buttons (999 = pill); default = borderRadius
}

// ── Dark mode (Tier 8C) ──────────────────────────────────────────────
// Published pages had zero support: `modern-dark` is a static PRESET, not a
// mode. Because every color already flows through `--pg-*` variables and
// per-section schemes, a mode is just a second palette re-declared under a
// media query / toggle class.
//
//   'off'    — single palette (today's behaviour; the default).
//   'auto'   — follow `prefers-color-scheme`, no visitor control.
//   'toggle' — follow the system by default, but render a switch that pins
//              the choice (persisted in localStorage).
export type DarkModeMode = 'off' | 'auto' | 'toggle'

export interface DarkModeConfig {
  mode?: DarkModeMode
  /**
   * The dark palette. Sparse — anything omitted is derived from the light
   * palette by the same contrast math `resolveTheme` already uses, so a
   * page gets a usable dark mode without picking 30 more colors.
   */
  palette?: PalettePreset
  /** Where the toggle renders when mode==='toggle'. */
  togglePosition?: 'header' | 'floating' | 'none'
  /** Storage key for the pinned choice. Default 'pg-theme'. */
  storageKey?: string
}

export interface PageTheme {
  preset?: string                // identifier of a built-in preset (modern, minimal, …)
  palette?: PalettePreset
  typography?: TypographyPreset
  layout?: PageLayoutPreset
  darkMode?: DarkModeConfig
  /**
   * Id of a PROJECT theme (`Page_Design/themes/<id>.json`). When set, the
   * backend MATERIALIZES that theme into this object (preset / palette /
   * typography / layout / darkMode) every time the page is loaded, saved or
   * published, so every render surface reads a complete theme with no extra
   * resolution — and every page bound to the same ref looks the same.
   * Absent: the page inherits its website's theme ref when it has no
   * palette of its own, else it is a plain preset theme as before.
   */
  ref?: string
  /**
   * The page's deliberate departures from its project theme (same shape,
   * sparse). Re-applied on top of the theme at every materialization, so
   * they survive a theme change. Discouraged — a look the whole site needs
   * belongs on the theme.
   */
  local?: Omit<PageTheme, 'ref' | 'local' | 'inherited'>
  /** Set by the backend when `ref` came from the page's WEBSITE (the page
   *  has no binding of its own) — it keeps following the site's theme. */
  inherited?: boolean
}

export type PageMode = 'web' | 'document'

// Top-level discriminator for what a page IS, picked once at creation
// and immutable thereafter. Drives navigator grouping, picker filters,
// and render strategy across the stack.
//   internal — login-gated page inside the Vision app (default)
//   external — public marketing page deployed to tenant.proteusvision.com/
//              (and tenant custom domains) via the static-site publisher
//   document — Smart Document template that spawns SmartDocument instances
// Supersedes `pageMode`. Legacy pages without `kind` are normalised on
// load: pageMode='document' → kind='document', else kind='internal'.
export type PageKind = 'internal' | 'external' | 'document'

// ── Per-project end-user auth (stored in application.json under `auth`) ──
// Read by the standalone runtime to decide which provider authenticates end
// users and whether internal pages + CRUD require login. `external` pages are
// always public regardless. Mirrored by the Python ProjectAuthConfig in
// runtime/server/.../bundles.py — keep in sync.
export type AuthProviderId = 'project-users' | 'twasta' | 'proteus_vision' | 'any'

export interface ProjectAuthConfig {
  providers: AuthProviderId[]   // which mechanism(s) authenticate end users
  requireLogin: boolean         // gate internal/document pages + CRUD
  defaultRoles?: string[]       // roles granted to a freshly registered user
  // Baseline object/action grants the app ships with. Seeded into the runtime
  // store on boot (insert-if-absent), so the deployed app carries its access
  // model. `actions: ["*"]` = all. (A "profile" is just a role name.)
  grants?: { role: string; obj_name: string; actions: string[] }[]
  // obj_name of a smart page (containing an `auth_form` block) to use as this
  // project's branded login screen. When set, the runtime sends unauthenticated
  // users to that page instead of the built-in /login screen.
  loginPage?: string
  // Whether visitors may self-register (project-users). When true, the login
  // screen offers a "create account" path; the first account becomes admin.
  allowRegistration?: boolean
  // Two-factor sign-in for this application's project users
  // (twasta_runtime_engine.identity.app_policy). Each customer organization's
  // admin may make it STRICTER for their people, never looser. Absent =
  // optional, 7 days' grace.
  mfa?: { mode: 'off' | 'optional' | 'required'; grace_days?: number }
  // Failed sign-in lockout (identity.lockout). Absent = the defaults.
  lockout?: {
    free_attempts?: number; base_delay_s?: number; max_delay_s?: number
    lock_after?: number; reset_after_s?: number
  }
}

// User-facing labels — keep in sync with PropertyPanel and any picker UI.
export const PAGE_KIND_LABELS: Record<PageKind, string> = {
  internal: 'Internal Page',
  external: 'External Site',
  document: 'Smart Document',
}

export interface DocumentDefaults {
  // Block id (from anywhere in the section/block tree) that should act as
  // the document's title at create time. The document's `title` is seeded
  // from this block's text on creation, but is editable independently
  // afterward.
  titleBlockId?: string
}

export interface PageModel {
  obj_name: string
  title: string
  description?: string
  inputs: PageInput[]
  blocks: Block[]                // v1 flat list — kept for backwards compat
  actions?: PageAction[]         // named click-action flows referenced by buttons
  dataSources?: PageDataSource[] // transaction-object data bindings (read-only display)
  // Runtime-injected: data baked at deploy/preview time, keyed by data
  // source id. Hydrated into the runtime `resources` map. Not authored.
  __resolved_data__?: Record<string, ResolvedDataEntry>
  // Runtime-injected: the before-load access decision the server attaches when
  // a page's `beforeLoad` event denied access or redirected. The client checks
  // this BEFORE rendering and redirects / shows access-denied. Not authored.
  __before_load__?: BeforeLoadDirective
  theme?: PageTheme              // page-level styling (palette, typography, layout)
  // ── v2 additions (all optional; v1 readers ignore them) ──
  pageSchemaVersion?: 1 | 2      // omitted => 1
  sections?: Section[]           // v2 hierarchy. When present, replaces top-level `blocks`
  designTokens?: DesignTokens    // global tokens (color schemes, type sets, spacing)
  seo?: PageSeo
  /** Page-level chrome + motion switches (back-to-top, scroll progress, …). */
  chrome?: PageChrome
  variants?: PageVariant[]       // A/B / multi-variant
  i18n?: I18nBundle[]            // locale → string map
  navigation?: PageNavigation    // site-level nav reference (resolved at site level)
  // Top-level discriminator (see `PageKind`). Mutable: authors flip it
  // from the page designer's Page panel or the prototype-screen kind
  // picker. The backend save endpoint stamps the latest resolved kind
  // back into the on-disk file on every save.
  kind?: PageKind
  // ── Before-load access event (optional) ──
  // Server-side business logic run at page-fetch time, BEFORE the page
  // renders, to decide whether the visitor may see it. Receives the URL
  // parameters, returns an allow/deny decision, an optional redirect (to
  // another in-app page or an external URL), and optional page-variable
  // values that are set before render. Enforced at the server page-fetch
  // endpoints on every surface — see `PageBeforeLoad`. Never gates the
  // designer canvas (which loads the page to EDIT, not to view).
  beforeLoad?: PageBeforeLoad
  // ── Document mode (Notion-like) — DEPRECATED, use `kind` ──
  // Kept for backwards compat with pages saved before `kind` existed.
  // Readers should call `normalizePageKind()` to resolve to a `PageKind`.
  pageMode?: PageMode
  /** Set when the page was cloned from a live website (see SectionSource). */
  source?: { url?: string; captured_at?: string; page_fingerprint?: string; draft_version?: number; chrome?: 'header' | 'footer' }
  documentDefaults?: DocumentDefaults
  // ── Runtime-add settings (kind='document' only) ──
  // When true, the runtime renders an "Add block" affordance at the end
  // of the document (and inside any container marked `runtimeAddable`)
  // so viewers with edit permission can append new blocks to their own
  // document instance. Defaults to true at create time for new document
  // pages; explicit false suppresses runtime-add even when individual
  // containers are marked addable.
  runtimeAddable?: boolean
  // Whitelist of block types the runtime add picker offers. Empty /
  // undefined = no restriction (all standard document blocks are
  // available). When set, only the listed `BlockType`s appear in the
  // picker.
  runtimeAddableTypes?: BlockType[]
  // Page-level policy for runtime block deletion. Defaults to
  // `'added_only'` when undefined (current behaviour: only blocks the
  // customer added at runtime can be removed; designer-placed blocks
  // are locked).
  //   'none'         — nothing can be deleted at runtime.
  //   'added_only'   — only runtime-added blocks (default).
  //   'marked'       — runtime-added + template blocks marked
  //                    `runtimeDeletable: true`.
  //   'empty'        — any block whose user-visible content is empty
  //                    (text, items, url, etc.) plus runtime-added.
  //   'any'          — any block, template or added (most permissive).
  runtimeDeletable?: RuntimeDeletePolicy
}

export type RuntimeDeletePolicy =
  | 'none' | 'added_only' | 'marked' | 'empty' | 'any'

// ── Page-level chrome & motion switches ──────────────────────────────
// Small page-wide affordances that aren't blocks because they float above
// the content and there is at most one of each.
export interface PageChrome {
  /** A "back to top" button once the visitor scrolls past a threshold. */
  backToTop?: boolean
  backToTopAfter?: number        // px scrolled before it appears; default 600
  backToTopPosition?: 'bottom-right' | 'bottom-left'
  /** A thin reading-progress bar pinned to the top of the viewport. */
  scrollProgress?: boolean
  scrollProgressColor?: string   // default --pg-primary
  scrollProgressHeight?: number  // px; default 3
  /**
   * Enable `scroll-behavior: smooth` for in-page anchor navigation. Opt-in
   * at page level so a document-style page can keep instant jumps.
   * Automatically suppressed under `prefers-reduced-motion`.
   */
  smoothScroll?: boolean
  /** Preload the page's LCP image (usually the hero) via <link rel=preload>. */
  preloadHeroImage?: boolean     // default true for external pages
  /** Kiosk behaviour for an always-on operational screen. Live surfaces only. */
  terminal?: PageTerminal
}

export interface PageRoot {
  page: PageModel
}

export interface BlockStyleRule {
  when: string                   // condition, e.g. "{{line.STATUS}} == 'LATE'"
  style?: BlockStyle             // layered over the block's wrapper style
  /**
   * Block SETTINGS to override while the rule holds — dotted paths, the same
   * paths a binding writes (`card.background`, `text`, `variant`). For what
   * the wrapper style cannot reach: a card paints its own surface, so a late
   * ticket's colour is `{"card.background": "#fde2e1"}`. String values may
   * carry `{{tokens}}`.
   */
  set?: Record<string, unknown>
}

/**
 * Terminal (kiosk) mode for an operational screen that stays on a wall or a
 * counter all day. Fullscreen and sound need a user gesture, so the page shows
 * a one-tap "Start" overlay that requests them; the screen wake lock is held
 * (and re-taken whenever the tab becomes visible again).
 */
export interface PageTerminal {
  fullscreen?: boolean
  wakeLock?: boolean
  sound?: boolean                // allow chimes (live sources' onNewRows)
  startLabel?: string            // overlay button caption
}

// ─── Before-load access event ─────────────────────────
// The BL function (authored in any of the project's BL languages and
// deployed collision-safe as `<page_obj>_before_load`) receives a payload
// `{_event:'before_load', url_params:{...}, _user:{...}}` and returns a
// decision the engine parses:
//   null / {} / {allowed:true}          → allow render
//   {allowed:false, message?}           → deny (no redirect)
//   {redirect:'https://...'}            → external redirect
//   {redirect:{page:'<obj>', params?}}  → in-app page redirect
//   {set_vars:{name:value,...}}         → set these page variables (the ONLY
//                                         values trusted from this flow)
// A blocking BL error (type 'E') is treated as a deny.
export type PageBlLanguage = 'plpgsql' | 'plsql' | 'python' | 'java'

export interface PageBeforeLoad {
  enabled?: boolean
  mode: 'business_logic'                 // (future: 'rest_api')
  // Same standardized shape as transaction validations / item_change
  // (`BusinessLogicValidation`): function name + description + spec +
  // optional per-function language (omitted = project default).
  business_logic: {
    method: string                       // bound function name (deploys as <page_obj>_<method>)
    language?: PageBlLanguage            // omit → project default BL language
    spec?: string                        // functional specification (drives codegen + file header)
    description?: string
  }
}

// Normalized redirect the server emits in `__before_load__`.
export type BeforeLoadRedirect =
  | { kind: 'external'; url: string }
  | { kind: 'page'; page: string; params?: Record<string, unknown> }

// Runtime-injected decision: present only when access was NOT granted as-is
// (a deny and/or a redirect). Absence means "render normally".
export interface BeforeLoadDirective {
  denied?: boolean
  redirect?: BeforeLoadRedirect
  message?: string
}

// ─── Page actions (declarative click handlers) ────────

export interface PageAction {
  id: string
  name: string                   // identifier referenced by ButtonClick.run_action
  description?: string
  steps: ActionStep[]
}

export type ActionStep =
  | { kind: 'validate'; names?: string[]; on_fail_focus?: boolean }
  | { kind: 'set_input'; name: string; value: string }              // value supports {{vars}}
  | { kind: 'clear_inputs'; names?: string[] }
  | { kind: 'set_field_error'; name: string; message: string }      // message supports {{vars}}
  | { kind: 'focus_input'; name: string }
  | { kind: 'show_message'; level: 'success' | 'error' | 'info'; text: string }
  | CallApiStep
  | TxnActionStep
  | TxnUpdateStep
  | RefreshSourceStep

/**
 * Run a transaction object's ACTION BUTTON on one or many records, through the
 * engine (rights, the button's own rules, approvals, audit, IOFlow events — as
 * if pressed on the transaction screen). `ids` is a token resolving to one id,
 * a comma-separated list or an array (`{{line._txn_id}}`, `{{sel_lines}}`);
 * more than one id runs the engine's bulk action. An empty list is a no-op.
 */
export interface TxnActionStep {
  kind: 'txn_action'
  obj_name: string
  action: string                 // the action button's name
  ids: string
  form_no?: string               // default '1' (the header form)
  refresh?: string[]             // data sources to re-read afterwards ([] = none; omitted = every runtime source)
  save_response_to?: string
  on_success?: ActionStep[]
  on_error?: ActionStep[]
}

/**
 * Change named columns of ONE record through the engine (validations, row
 * security, approval divert, audit). Only the listed columns are written.
 * `expect` is compare-and-set: the save is refused (code RECORD_CHANGED) when
 * the STORED row no longer holds these values — two people tapping the same
 * item cannot both win.
 */
export interface TxnUpdateStep {
  kind: 'txn_update'
  obj_name: string
  id: string                     // token → the record's _txn_id
  values: Record<string, string> // column → value ({{tokens}})
  expect?: Record<string, string>
  form_no?: string
  refresh?: string[]
  save_response_to?: string
  on_success?: ActionStep[]
  on_error?: ActionStep[]
}

/** Re-read data sources now. Omitted/empty `sources` = every runtime source. */
export interface RefreshSourceStep {
  kind: 'refresh_source'
  sources?: string[]
}

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface CallApiStep {
  kind: 'call_api'
  method: HttpMethod
  url: string                                                       // {{vars}}
  // IOFlow-routed API (from the project's Integration design). When set, the
  // call POSTs through the surface's server-side /integration/invoke proxy
  // (the browser never holds the IOFlow invocation key) and `url`/`headers`
  // are ignored (kept as documentation/fallback for unsynced hosts).
  ioflow_api?: string
  headers?: Record<string, string>                                  // values support {{vars}}
  body?: unknown                                                    // JSON; defaults to typed inputValues for non-GET
  save_response_to?: string                                         // exposes {{<key>.dotted.path>}}
  save_response_var?: string                                        // writes the parsed response OBJECT into this page variable (structure preserved, not stringified) — e.g. to feed an embed's recordVar
  on_success?: ActionStep[]
  on_error?: ActionStep[]
}

// (Designer-internal `Selection` / `SelectionType` / `LayoutMode` live in
// frontend/src/pages/designers/page/types.ts — they describe designer UI
// state, not page-model shape, and are not part of this package.)

// ════════════════════════════════════════════════════════════════════
// V2 SCHEMA — additive. v1 readers see undefined for these fields and
// keep working; v2 readers prefer `sections` over flat `blocks` when
// present. The migrator at load time converts v1 → v2 in memory.
// ════════════════════════════════════════════════════════════════════

export const PAGE_SCHEMA_VERSION = 2 as const

export type Viewport = 'desktop' | 'tablet' | 'mobile'

// ── Section layout primitives ────────────────────────────────────────
// Everything used to render inside the single `--pg-content-width` column,
// which is the main reason stock output reads as templated: every section
// was a fixed-width column in one vertical stack.
//
//   'contained' (default) — the page content column, as before.
//   'full'                — edge-to-edge; the section's own background bleeds
//                           to the viewport edges while its CONTENT stays in
//                           the content column (the standard "full-bleed
//                           band" pattern).
//   'breakout'            — wider than the content column but not full-bleed
//                           (content column + ~15%), for feature imagery.
export type SectionWidth = 'contained' | 'full' | 'breakout'

// SVG shape dividers drawn at the top and/or bottom edge of a section — the
// wave/slant/curve transitions that separate bands on a modern site.
export type ShapeDividerKind =
  | 'none' | 'wave' | 'slant' | 'curve' | 'triangle' | 'arrow' | 'zigzag' | 'book'

export interface ShapeDivider {
  kind: ShapeDividerKind
  height?: number                // px, default 60
  color?: string                 // defaults to the NEXT surface color
  flipX?: boolean
  /** Draw the divider on top of the section's content instead of behind it. */
  above?: boolean
}

// A purely decorative layer inside a section — blobs, glows, dot grids and
// noise are what fill the negative space on a designed page. All are CSS/SVG
// only and safely additive: a missing decoration still leaves a valid page.
export type DecorationKind = 'blob' | 'glow' | 'dot-grid' | 'grid-lines' | 'noise' | 'rings'
export type DecorationCorner =
  | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center'

export interface Decoration {
  id: string
  kind: DecorationKind
  corner?: DecorationCorner      // default 'top-right'
  size?: number                  // px, default 320
  color?: string                 // defaults to --pg-primary
  opacity?: number               // 0..1, default 0.16
  blur?: number                  // px, default 60 for blob/glow
  offsetX?: number               // px nudge from the anchored corner
  offsetY?: number
}

// ── Section: Shopify-style top-level grouping ────────────────────────
export interface Section {
  id: string                     // sec_<base36>
  name?: string                  // human label shown in the section tree
  tag?: 'header' | 'main' | 'footer' | 'aside' | 'popup' | 'custom'
  blocks: Block[]
  style?: BlockStyle
  visibility?: BlockVisibility
  presetId?: string              // origin saved-section preset, if any
  colorSchemeId?: string         // applies a named color scheme to this section
  enabledOn?: string[]           // template/group targeting (Shopify-style)
  disabledOn?: string[]
  lazy?: boolean                 // defer render until enters viewport (P5 perf)
  blockGap?: number              // px override for vertical space between blocks (falls back to theme.layout.blockGap)
  popup?: SectionPopup           // only for tag==='popup': trigger + modal chrome. Section blocks render INSIDE the modal.
  // ── Layout & composition (Tier 3) ──
  anchorId?: string              // `scroll_to` / `#hash` target; emitted as the element id
  width?: SectionWidth           // contained | full | breakout (default contained)
  minHeight?: number | 'screen'  // px, or 'screen' for 100svh — turns any section into a hero canvas
  verticalAlign?: 'top' | 'center' | 'bottom'   // content placement when minHeight leaves slack
  dividerTop?: ShapeDivider
  dividerBottom?: ShapeDivider
  decorations?: Decoration[]
  // Pin the section to the top of the viewport while it is in view. Until
  // now sticky existed only for the SITE header, not for sections.
  sticky?: boolean
  stickyOffset?: number          // px from the top when sticky, default 0
  source?: SectionSource         // clone provenance (website re-sync)
}

// ── Visibility / Animation ───────────────────────────────────────────
export interface BlockVisibility {
  devices?: Viewport[]                    // empty/undefined = all
  roles?: string[]                        // user roles allowed
  schedule?: { start?: string; end?: string }   // ISO timestamps
  condition?: string                      // {{var}} expression, evaluated at runtime
}

export type AnimationPreset =
  | 'none' | 'fade' | 'fade-up' | 'fade-down' | 'fade-left' | 'fade-right'
  | 'scale-in' | 'slide-up' | 'slide-down' | 'zoom-in'
  | 'blur-in' | 'clip-wipe'

/**
 * Block entrance motion.
 *
 * Implemented as CSS keyframes plus (for the scroll/enter triggers) one
 * shared ~20-line IntersectionObserver per page — deliberately NOT
 * `animation-timeline: view()`, whose support is still uneven.
 *
 * Accessibility contract, enforced by the emitter: every keyframe lives
 * inside `@media (prefers-reduced-motion: no-preference)` and the reveal
 * script no-ops under `reduce`, so the block's BASE state is always the
 * final, visible one. Animation is an enhancement — a JS failure or an
 * unsupported browser can never leave the page blank.
 */
export interface BlockAnimation {
  preset: AnimationPreset
  on?: 'enter' | 'scroll' | 'load' | 'hover'
  delay?: number                 // ms
  duration?: number              // ms
  easing?: 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear'
  once?: boolean                 // play once vs. each enter
  // Per-child delay in ms: child N starts at `delay + N × stagger`. This is
  // what makes a card grid or feature list read as designed rather than
  // rendered. Implemented as one CSS custom property multiplied by the
  // child's index, so it costs nothing beyond the base mechanism.
  stagger?: number
}

// ── BindingExpr: dynamic source lightning-bolt UI ────────────────────
export type BindingSourceKind =
  | 'static'
  | 'page_input'
  | 'metafield'                  // tenant-defined custom property
  | 'resource_field'             // dotted path into a transaction/dashboard/visual result
  | 'data_field'                 // dotted path into a PageDataSource result (record mode)
  | 'item_field'                 // a column of the current/named repeater row
  | 'expression'                 // {{var}} expression with light arithmetic

export interface BindingExpr {
  source: BindingSourceKind
  name?: string                  // for page_input / metafield
  value?: unknown                // for static
  path?: string                  // for resource_field / data_field / item_field (dotted)
  resource?: { obj_type: string; obj_name: string }   // for resource_field
  dataSource?: string            // for data_field: PageDataSource.id (record mode)
  scope?: string                 // for item_field: named enclosing scope alias
  expression?: string            // for expression
  fallback?: unknown
  /**
   * Turn a list of ROWS into a block's list ITEMS: `{itemKey: "COLUMN"}` per
   * row, e.g. a testimonial carousel fed by a data source —
   * `{"source": "data_field", "dataSource": "ds_t", "path": "rows",
   *   "map": {"quote": "QUOTE", "authorName": "AUTHOR_NAME"}}` bound to
   * `items`. Columns match case-insensitively; an item gets `id` (the row's
   * mapped id, else its position) so new rows simply appear.
   */
  map?: Record<string, string>
}

export type BlockBindings = Record<string, BindingExpr>

// ─── Transaction data binding ───────────────────────────────────────
// Read-only display of a transaction OBJECT's data inside ordinary
// presentation blocks (distinct from the interactive `embed` block).
// See docs/smart-page-data-binding-plan.md.

export type BindingTime = 'deployment' | 'runtime'

export interface DataSourceSort {
  column: string
  dir: 'asc' | 'desc'
}

// Filter-value reference for a data source. Like FilterValueRef but also
// supports 'item_field' for CORRELATED (nested) sources, where the value
// comes from a column of an enclosing repeater row.
export type DataFilterSource = 'page_input' | 'static' | 'item_field'

export interface DataFilterValueRef {
  source: DataFilterSource
  name?: string                  // page_input → input name; item_field → row column
  value?: string                 // static literal
  scope?: string                 // item_field only: named enclosing scope alias
}

export interface DataSourceFilter {
  id?: string
  column: string
  /** `in` = any of a list: `value` is a comma-separated list ("P001, P007")
   *  or an array; an empty list matches nothing. Data sources only. */
  op: FilterOp | 'in'
  value?: DataFilterValueRef
  from?: DataFilterValueRef
  to?: DataFilterValueRef
}

// Object types a page data source may read.
//   'T' — a transaction object (the original, and still the default).
//   'V' — a visual's result set (read-only rows, honours its schema security).
//   'G' — a general-process result.
//   'api' — an arbitrary REST endpoint returning a row array.
// Widening this is what lets a collection page be backed by something other
// than a transaction table.
export type DataSourceObjType = 'T' | 'V' | 'G' | 'api'

export interface PageDataSource {
  id: string                              // stable key, e.g. "ds_products"
  resource: { obj_type: DataSourceObjType; obj_name: string }
  /** obj_type 'api': the endpoint + how to find the rows in its response. */
  url?: string
  rowsPath?: string                       // dotted path to the row array
  form_no?: string                        // multi-form transactions
  mode: 'list' | 'record'                 // collection vs single row
  bindingTime: BindingTime                // default 'deployment'
  filters?: DataSourceFilter[]            // values may ref page input or enclosing row
  sort?: DataSourceSort[]
  limit?: number                          // list mode; clamped server-side
  // record mode: how to pick the single row
  record?:
    | { by: 'first' }
    | { by: 'txn_id'; value: DataFilterValueRef }
  // Column allowlist — REQUIRED for external/runtime sources. Only these
  // columns are queried/returned (bounds the public surface).
  columns?: string[]
  /**
   * Runtime source of the visitor's OWN rows (a per-account object): not
   * fetched while nobody is signed in — it would only be refused — and
   * fetched as soon as someone signs in. Until then it reads as loaded and
   * empty (`{{id._loaded}}` true, every column '').
   */
  signedInOnly?: boolean
  /**
   * Content collections: return only records that are publicly visible NOW
   * (the object's content profile — status in its live values, publish date
   * reached), newest first, with the derived `_url` / `_excerpt` / `_image` …
   * fields. Drafts never reach a public page through this source.
   */
  contentLive?: boolean
  /**
   * Keep a `runtime` source current while the page is open: re-read every
   * `intervalSeconds` (minimum 5) while the tab is visible. Live surfaces only
   * — a prerendered static page ignores it.
   */
  refresh?: DataSourceRefresh
}

export interface DataSourceRefresh {
  intervalSeconds?: number       // default 15, minimum 5
  keyColumn?: string             // row identity; default _txn_id, then id
  /** Rows that were not there on the previous read. */
  onNewRows?: {
    highlightSeconds?: number    // pulse the new copies (default 6; 0 = off)
    chime?: boolean              // play a short tone (needs chrome.terminal.sound or a prior tap)
    action?: string              // page action run once per arrival batch ({{new_rows}} = count)
  }
}

// Repeat a block's item template over a list data source's rows.
//
// Implemented on EVERY surface — designer canvas, in-app preview, static SSR
// and the deployed runtime. (It was previously honored only by the deployed
// runtime, so a data-driven page rendered a single item on the canvas and
// silently broke on publish.)
/**
 * How a repeat lays its copies out.
 *
 * Until this existed a repeat emitted a FLAT sequence, so binding a card to
 * a product table produced one card per row stacked down the page — which
 * is why a catalogue, the commonest thing anyone binds, could not be
 * data-bound and still look like a catalogue. `mode: 'flow'` is that
 * original behaviour and stays the default; `grid` wraps the copies in the
 * same responsive grid `card_grid` uses, so the breakpoints match.
 */
export interface RepeatLayout {
  mode?: 'flow' | 'grid' | 'board' | 'positioned'
  columns?: ColumnsCount                  // grid: desktop track count (default 3)
  gap?: number                            // px between copies (default 20)
  /** grid: min track width in px before the column count drops. Default 220. */
  minColumnWidth?: number
  // ── board: copies grouped into lanes by a column's value ──
  groupBy?: string                        // column whose value picks the lane
  /** Fixed lanes, in order. Omitted ⇒ one lane per distinct value, in row order. */
  lanes?: RepeatLane[]
  laneWidth?: number                      // px; default 280
  /** Blocks drawn at the top of each lane, with scope alias `lane`:
   *  {{lane.value}} {{lane.label}} {{lane.count}} {{lane.ids}} and every
   *  column of the lane's FIRST row ({{lane.TABLE_NO}}). */
  laneHeader?: Block[]
  /** Page action run when a card is dragged to another lane — the row is in
   *  scope (`as`) and {{drop.lane}} is the target lane's value. */
  dropAction?: string
  // ── positioned: each copy placed at its row's own coordinates ──
  x?: string                              // column holding x (design units)
  y?: string
  w?: string                              // column or fixed number; default 100
  h?: string                              // default 80
  canvasWidth?: number                    // design-unit width of the plane (default 1000)
  canvasHeight?: number                   // default 600
  /** Below the tablet breakpoint: keep scaling (`scale`) or fall back to a grid. */
  mobile?: 'scale' | 'grid'
}

export interface RepeatLane {
  value: string
  label?: string
  color?: string
}

/** Pick repeated copies. The page variable holds the chosen keys joined by commas. */
export interface RepeatSelect {
  mode: 'single' | 'multi'
  var: string                             // page variable to write
  key?: string                            // row column; default _txn_id, then id
}

export interface BlockRepeat {
  source: string                          // PageDataSource.id (mode:'list')
  as?: string                             // scope alias for nested references
  /** Cap the rendered rows (independent of the source's own `limit`). */
  max?: number
  /** Lay the copies out as a grid / board / plane rather than a flat stack. */
  layout?: RepeatLayout
  /** Tap a copy to select it (when the block has no `on_click` of its own). */
  select?: RepeatSelect
  /** Paginate the repeated list rather than emitting every row. */
  pagination?: ListPagination
  /**
   * How many placeholder copies the DESIGN CANVAS draws when it has no data
   * (the canvas has no live rows). Default 3 — enough to see the layout.
   */
  designCount?: number
}

// Runtime-injected resolved data (baked at deploy/preview or fetched
// live). Keyed by PageDataSource.id. Never authored by hand.
export interface ResolvedDataEntry {
  mode?: 'list' | 'record'
  rows?: Record<string, unknown>[]
  row?: Record<string, unknown> | null
  total?: number
  correlated?: boolean                    // nested: rows are under `by`
  by?: Record<string, Record<string, unknown>[]>
}

// ── Design tokens (theme-level) ──────────────────────────────────────
// A ColorScheme is the per-section override version of a palette. It
// declares the same hierarchy as PalettePreset; the section wrapper
// publishes these as `--pg-*` CSS vars so every block inside picks up
// the override. Optional fields auto-derive from the explicit ones at
// resolve time, mirroring PalettePreset.
export interface ColorScheme {
  id: string                     // 'cs_primary'
  name: string                   // human label
  /** Written by the backend when the scheme comes from the page's project
   *  theme; such schemes are replaced on every materialization. */
  fromTheme?: boolean
  background: string
  surface: string
  text: string
  textSecondary: string
  primary: string
  onPrimary: string
  accent: string
  border: string
  // ── Optional extended hierarchy (mirrors PalettePreset) ──
  surfaceAlt?: string
  surfaceMuted?: string
  elevated?: string
  borderStrong?: string
  heading?: string
  muted?: string
  subtle?: string
  link?: string
  linkHover?: string
  onSurface?: string
  onSurfaceAlt?: string
  onAccent?: string
  inverted?: string
  info?: string
  infoBg?: string
  success?: string
  successBg?: string
  warning?: string
  warningBg?: string
  error?: string
  errorBg?: string
}

export interface SpacingTokens {
  xs: number; sm: number; md: number; lg: number; xl: number; '2xl': number
}
export interface RadiiTokens {
  none: number; sm: number; md: number; lg: number; full: number
}
export interface ShadowTokens {
  none: string; sm: string; md: string; lg: string; xl: string
}

/**
 * Library of named color schemes that individual sections can opt into via
 * `Section.colorSchemeId`. Page-wide colors and typography live on
 * `PageTheme` (page.theme.palette / page.theme.typography) — DesignTokens
 * never sets a page-wide default.
 */
export interface DesignTokens {
  colorSchemes?: ColorScheme[]
  spacing?: SpacingTokens
  radii?: RadiiTokens
  shadows?: ShadowTokens
}

// ── SEO ──────────────────────────────────────────────────────────────
// EVERY field here is emitted by the static-site renderer. (It historically
// emitted only og:title + og:image; ogDescription/twitterCard/jsonLd were
// authored in the SEO panel and silently discarded.)
export interface PageSeo {
  title?: string
  description?: string
  ogImage?: string
  ogTitle?: string
  ogDescription?: string
  ogType?: string                // 'website' (default) | 'article' | …
  ogUrl?: string                 // absolute; falls back to `canonical`
  twitterCard?: 'summary' | 'summary_large_image'
  twitterSite?: string           // @handle
  twitterCreator?: string        // @handle
  canonical?: string
  robots?: string                // e.g. 'index,follow' or 'noindex'
  jsonLd?: unknown               // schema.org structured data (object or array)
  /** Exclude this page from the generated sitemap.xml. */
  noSitemap?: boolean
  /** Sitemap hints. */
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never'
  priority?: number              // 0.0..1.0
  /** Open Graph article meta (og:type 'article') — set by the publisher for
   *  content-collection pages from the record's dates/author/terms. */
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
    section?: string
    tags?: string[]
  }
  /** Absolute URLs of the previous / next page of a paginated list. */
  prev?: string
  next?: string
}

// ── Site-level publish settings (Tier 7) ─────────────────────────────
// Carried on the site manifest and consumed by the static-site renderer +
// publisher. Distinct from PageSeo, which is per page.
export interface SiteAnalytics {
  //   'ga4'      — gtag.js with `id` as the measurement id (G-XXXX)
  //   'gtm'      — Google Tag Manager container (GTM-XXXX)
  //   'plausible'— plausible.io script; `domain` is the site domain
  //   'umami'    — self-hosted umami; `src` + `id`
  //   'custom'   — a raw <script> body supplied in `custom`
  provider: 'ga4' | 'gtm' | 'plausible' | 'umami' | 'custom'
  id?: string
  domain?: string
  src?: string
  custom?: string
  /** Auto-track button clicks and form submits as events. Default true. */
  autoEvents?: boolean
  /**
   * Hold the script until the visitor accepts the matching cookie-consent
   * category. When set, nothing loads before consent is stored.
   */
  requiresConsent?: string       // ConsentCategory.id
}

export interface SiteSettings {
  /** URL of the favicon (any browser-supported format). */
  favicon?: string
  /** Apple touch icon (180×180 png). Falls back to `favicon`. */
  appleTouchIcon?: string
  /** BCP-47 language tag emitted as <html lang>. Default 'en'. */
  lang?: string
  /** Text direction. Default 'ltr'. */
  dir?: 'ltr' | 'rtl'
  /** Absolute site origin, e.g. 'https://acme.com'. Needed for absolute
   *  og:url / canonical / sitemap entries. */
  siteUrl?: string
  /** og:site_name. */
  siteName?: string
  analytics?: SiteAnalytics
  announcement?: AnnouncementBar
  /**
   * Absolute base URL of the Twasta API, e.g. 'https://app.twasta.ai/api'.
   *
   * The published static site is otherwise entirely self-contained (its data
   * is baked at deploy time), so it has no way to reach the backend. Setting
   * this is what lets a FORM on a public page actually submit — without it a
   * static contact form renders but goes nowhere, which is the exact defect
   * the built-in submissions store exists to fix. The deploy pipeline sets
   * it; the tenant's site origin must be in the API's CORS allow-list.
   */
  apiBase?: string
  /** Owner user id + project, needed to address the submissions endpoint. */
  ownerUserId?: string
  project?: string
  /** Generate sitemap.xml + robots.txt at publish time. Default true. */
  sitemap?: boolean
  /** Extra robots.txt lines appended after the generated block. */
  robotsExtra?: string
  /**
   * Legacy URLs kept alive after a move (e.g. a WordPress site's root-level
   * post URLs now under `/blog/`). `from` is a path on this site, `to` a site
   * path or an absolute URL. Written as redirect pages at publish time; a path
   * a real page occupies is never shadowed.
   */
  redirects?: { from: string; to: string }[]
  /** Default social image used by any page without its own `seo.ogImage`. */
  defaultOgImage?: string
  /** Organisation logo URL — the publisher of structured data (articles). */
  logo?: string
  /** Default ISO currency for product structured data. */
  currency?: string
  /** RSS feeds advertised on every page (<link rel="alternate">). Set by the
   *  publisher for content collections with a feed. */
  feeds?: { title: string; href: string }[]
  /**
   * reCAPTCHA v3 site key, stamped in by the deploy pipeline from the
   * project's (or the instance's) Spam protection settings.
   *
   * Baked rather than fetched because the published site is static: a form on
   * it has to be able to execute the check before it has spoken to any API,
   * and one more round trip on a contact page is a round trip that can fail.
   * Publishable by nature — the browser executes with it.
   */
  recaptchaSiteKey?: string
  /**
   * First-party activity analytics, stamped by the deploy from the project's
   * Advanced → Activity switches. Present ⇒ every published page embeds the
   * shared tracker posting to `endpoint` (`<apiBase>/activity/collect`).
   */
  activity?: SiteActivitySettings
}

export interface SiteActivitySettings {
  endpoint: string
  consent?: 'banner' | 'none'
  captureClicks?: boolean
  captureScroll?: boolean
}

// ── Variants (A/B) ───────────────────────────────────────────────────
export interface PageVariant {
  id: string                     // 'var_<base36>'
  name: string
  weight: number                 // 0-100
  sections?: Section[]           // when present, replaces base sections
  blockOverrides?: Record<string, Partial<Block>>   // patch by blockId
  active?: boolean
  startedAt?: string             // ISO
  endedAt?: string
}

// ── i18n ─────────────────────────────────────────────────────────────
export interface I18nBundle {
  locale: string                 // 'en', 'fr-CA', etc.
  strings: Record<string, string>
}

// ── Navigation reference ─────────────────────────────────────────────
export interface PageNavigation {
  menuId?: string                // refers to a project-level menu
  // ── Site-wide chrome control (read by every render surface) ──
  // For pages that belong to an External Website (X-node), the site's
  // `header_page_obj_name`/`footer_page_obj_name` chrome is applied to
  // every page by default. These fields let ONE page override that:
  //   showHeader/showFooter === false  → suppress the site header/footer
  //                                       on this page (e.g. a landing or
  //                                       login page).
  //   headerPageObjName/footerPageObjName → replace the site header/footer
  //                                       with a DIFFERENT chrome page on
  //                                       this page only. `showHeader:false`
  //                                       wins over a replacement.
  // Omitted (undefined) === inherit the site-wide chrome. Renderers treat
  // only an explicit `=== false` as "hide".
  showHeader?: boolean
  showFooter?: boolean
  headerPageObjName?: string     // per-page replacement header chrome page
  footerPageObjName?: string     // per-page replacement footer chrome page
  headerSectionId?: string       // section.tag === 'header' (intra-page)
  footerSectionId?: string
}

// ── Custom-code & form blocks ────────────────────────────────────────

// Page-bridge capabilities a custom_code/custom_html block may be granted.
// The block's JS stays inside the sandboxed iframe; a postMessage bridge
// (window.twasta, injected by the host) exposes ONLY what is allowlisted
// here. Everything defaults to OFF — an empty/missing list grants nothing;
// the sentinel '*' grants all names of that kind. Interactive surfaces
// (in-app preview, standalone runtime) serve the live bridge; the static
// SSR surface bakes a read-only stub (vars snapshot, no actions).
export interface CustomCodeBridgeConfig {
  enabled?: boolean
  vars?: string[]                // page-var names readable + writable ('*' = all)
  actions?: string[]             // page-action names runnable ('*' = all)
  navigate?: boolean             // allow twasta.navigate(objType, objName)
  data?: string[]                // data-source names/ids readable ('*' = all)
}

// A Claude Design component (.dc.html) carried into a page VERBATIM: the
// <x-dc> template + `class Component extends DCLogic { renderVals() }` run by
// the live dc runtime (page-runtime/src/dc/dcRuntime.js) inside the block's
// sandboxed frame — infographics computed in renderVals, charts drawn in
// componentDidMount, timers, clickable tabs.
export interface CustomComponentSpec {
  format: 'dc'
  source: string                 // the component's .dc.html (pictures = public URLs)
  props?: Record<string, unknown> // over the source's data-props defaults
  children?: Record<string, string> // sources of the <dc-import>ed children, by name
}

export interface CustomCodeBlock extends BaseBlock {
  type: 'custom_code'
  html?: string
  css?: string
  js?: string
  sandbox?: boolean              // default true — render in sandboxed iframe
  bridge?: CustomCodeBridgeConfig // opt-in page bridge (window.twasta)
  // ── Custom component extras (sandboxed frame only) ──
  libs?: string[]                // script libraries, https on cdnjs / jsdelivr / unpkg only
  component?: CustomComponentSpec // a Claude Design component rendered verbatim
  height?: number                // px — a FIXED frame height; omitted = fits its content
}

// ── Custom HTML block ────────────────────────────────────────────────
// Raw HTML escape hatch. Shares the rendering machinery with
// `custom_code` (a sandboxed iframe runs any `js`/`css`; with
// `sandbox: false` the HTML renders inline with <script> tags stripped).
// `custom_html` exists as a distinct, friendlier "Custom HTML" block so
// authors (and the AI assistant, which historically emitted this type)
// have a first-class raw-HTML block. Prefer real semantic blocks
// (image, button_group, auth_form, …) for headers/footers — reach for
// this only when no block fits. Identical shape to CustomCodeBlock so
// the two can share renderers and migrators interchangeably.
export interface CustomHtmlBlock extends BaseBlock {
  type: 'custom_html'
  html?: string
  css?: string
  js?: string
  sandbox?: boolean              // default true — render in sandboxed iframe
  bridge?: CustomCodeBridgeConfig // opt-in page bridge (window.twasta)
  // ── Custom component extras (sandboxed frame only) ──
  libs?: string[]                // script libraries, https on cdnjs / jsdelivr / unpkg only
  component?: CustomComponentSpec // a Claude Design component rendered verbatim
  height?: number                // px — a FIXED frame height; omitted = fits its content
}

// ── Navigation menu block ────────────────────────────────────────────
// A horizontal navigation menu with MULTI-LEVEL dropdown submenus. Put it
// in a site header page so the website's navigation renders consistently
// on every page. Folders in the site's nav tree become dropdown submenus;
// nested folders become nested submenus (unbounded depth).
//   source: 'site' (default) — auto-render the parent External Website's
//           nav tree (its pages + '-' folders as submenus). The menu data
//           is supplied by each render surface (in-app/SSR/runtime/canvas)
//           so links and labels always match the live site. No `items`
//           needed.
//   source: 'custom' — render the authored `items` tree instead.
//   menu:   with source 'site', render one NAMED SITE MENU (see SiteMenuEntry)
//           instead of the nav tree — the way to say "these links in the
//           header, those in the footer".
export interface NavMenuItem {
  id: string
  label: string
  href?: string                  // site-internal "/slug", "#anchor", or an external URL
  children?: NavMenuItem[]        // submenu items (recursive)
  // ── Mega-menu extras (rendered only when the parent uses `mega` layout) ──
  description?: string           // secondary line under the label
  icon?: string                  // icon name shown beside the label
  badge?: string                 // small pill, e.g. 'New'
  featured?: boolean             // renders in the panel's highlighted rail
}
export interface NavMenuBlock extends BaseBlock {
  type: 'nav_menu'
  source?: 'site' | 'custom'     // default 'site'
  items?: NavMenuItem[]          // used when source === 'custom'
  align?: Align                  // left | center | right (default left)
  includeHome?: boolean          // site mode: include the home link (default true)
  // site mode: render the site's NAMED menu (`functional_specs.menus.<name>`
  // on the External Website node, e.g. 'header' / 'footer'). Takes precedence
  // over `root`. On a site that defines menus, a name it does not define
  // renders no links; a site with NO menus shows the whole tree instead.
  menu?: string
  // LEGACY site mode: pin the menu to a nav FOLDER (its obj_name or visible
  // label) — the old "Default Header" / "Default Footer" convention. Kept so
  // existing sites render; new sites use `menu`. Omitted = the whole tree.
  root?: string
  // ── Mobile navigation (Tier 4.1) ──
  // Below `mobileBreakpoint` the horizontal link row collapses behind a
  // hamburger that opens a drawer. Implemented with the checkbox technique
  // so it works on the STATIC site with no JS and no hydration; interactive
  // surfaces layer Escape-to-close and body-scroll-lock on top.
  // `mobile: 'none'` keeps the legacy wrapping row.
  mobile?: 'drawer' | 'none'                 // default 'drawer'
  mobileBreakpoint?: number                  // px; default 768
  drawerSide?: 'left' | 'right' | 'full'     // default 'right'
  drawerLabel?: string                       // a11y label; default 'Menu'
  // ── Dropdown layout (Tier 4.5) ──
  // 'list' (default) is the current fixed-width column; 'mega' renders a
  // full-width multi-column panel with description + icon per item.
  dropdownLayout?: 'list' | 'mega'
  megaColumns?: 2 | 3 | 4                    // default 3
  // ── Active-link highlighting (Tier 4.3) ──
  // Marks the link matching the current page. With `scrollSpy`, anchor links
  // (#hash) additionally track which section is currently on screen.
  highlightActive?: boolean                  // default true
  scrollSpy?: boolean                        // default true when any item href is a '#anchor'
}

// ── Site menus ────────────────────────────────────────────────────────
// Menus are SITE data, authored on the External Website node as
// `functional_specs.menus = { <name>: SiteMenuEntry[] }` and rendered by a
// `nav_menu` block with `menu: <name>`. Where a page sits in the nav tree no
// longer decides which menu shows it, and a page may be in several menus.
// An entry is one of:
//   { page }               a site page by obj_name — URL + default label are
//                          resolved by each render surface, so never stale
//   { label, href }        a custom link ("/blog/feed.xml", an outside URL)
//   { label, children }    a dropdown (a column heading in a footer)
export interface SiteMenuEntry {
  id?: string                    // stable key for the editor; not rendered
  page?: string                  // obj_name of a page of this site
  label?: string                 // overrides the page's own label
  href?: string
  children?: SiteMenuEntry[]
}
export type SiteMenusSpec = Record<string, SiteMenuEntry[]>

// ── Breadcrumb ────────────────────────────────────────────────────────
// The trail at the top of a deep page. It is its own block rather than a
// `nav_menu` with two items because it means something different to a
// reader and to a crawler: it emits `<nav aria-label="Breadcrumb">` plus a
// `BreadcrumbList` JSON-LD, which is the structured data search engines use
// to show a path instead of a bare URL.
export interface BreadcrumbItem {
  id: string
  label: string
  /** Site-internal "/slug", "#anchor" or an absolute URL. The LAST item is
   *  the current page and is rendered as plain text even if it has one. */
  href?: string
}

export interface BreadcrumbBlock extends BaseBlock {
  type: 'breadcrumb'
  items: BreadcrumbItem[]
  /** Between the items. Default '/'. */
  separator?: string
  align?: Align
  /** Emit the BreadcrumbList JSON-LD. Default true. */
  structuredData?: boolean
}

// ── Filter bar ────────────────────────────────────────────────────────
//
// Search, facets and sort over a SIBLING block's items, entirely on the
// client. A published catalogue could not be filtered at all: `input_group`
// is dropped by the static prerender (no session, no live data) and nothing
// filtered an authored grid in any case, so the only way to narrow a
// published list was to navigate to a different page.
//
// It reads the target's items from the DOM (`.pg-item`, the same marker the
// item-scoped hover uses) and the facet values from the `data-pg-facet-*`
// attributes the item renderers emit, so facet values and their counts are
// DERIVED from what is on the page — nothing is authored twice, and a facet
// can never list a value no item has.
//
// With JavaScript off every item stays visible, which is also the state a
// crawler sees.

/** Which rendered part of an item a facet groups by. */
export type FilterFacetSource = 'eyebrow' | 'badge' | 'status'

export interface FilterFacet {
  id: string
  label: string
  from: FilterFacetSource
  /** Show how many items carry each value. Default true. */
  showCounts?: boolean
  /** Label for the "no filter" choice. Default 'All'. */
  allLabel?: string
}

export interface FilterSortOption {
  id: string
  label: string
  /** An item's `data-pg-sort-<by>` value. 'price' and 'title' are emitted
   *  by the card renderer; 'none' restores the authored order. */
  by: 'price' | 'title' | 'none'
  dir?: 'asc' | 'desc'
}

export interface FilterSearchConfig {
  placeholder?: string
  label?: string
}

export interface FilterBarBlock extends BaseBlock {
  type: 'filter_bar'
  /** The `id` of the block whose items this filters. */
  target: string
  layout?: 'sidebar' | 'inline'
  search?: FilterSearchConfig
  facets?: FilterFacet[]
  sort?: FilterSortOption[]
  /** `{n}` is replaced with how many items are showing. */
  resultLabel?: string
  /** Shown when a search or facet leaves nothing. */
  emptyText?: string
}

// ── Cart ──────────────────────────────────────────────────────────────
//
// A basket on a published page. See `page-runtime/cart.ts` for why it is a
// basket and never a price authority.

export interface CartBadgeBlock extends BaseBlock {
  type: 'cart_badge'
  label?: string
  /** Where the badge links. Default '/cart'. */
  href?: string
  icon?: string
  /** Hide the count entirely when the cart is empty. Default true. */
  hideWhenEmpty?: boolean
  variant?: ButtonVariant
  /** Icon with the count on its corner, no visible text — for a compact
   *  header. `label` becomes the link's accessible name. */
  iconOnly?: boolean
}

export interface CartLinesBlock extends BaseBlock {
  type: 'cart_lines'
  emptyText?: string
  continueLabel?: string
  continueHref?: string
  /** Show the per-line quantity stepper. Default true. */
  editable?: boolean
}

export interface CartSummaryBlock extends BaseBlock {
  type: 'cart_summary'
  heading?: string
  subtotalLabel?: string
  /** Tax is DECLARED, not asked for: a static page has no server to ask. */
  taxPercent?: number
  taxLabel?: string
  shippingFlat?: number
  shippingFreeOver?: number
  shippingLabel?: string
  freeLabel?: string
  totalLabel?: string
  checkoutLabel?: string
  /** Site-internal "/checkout" or an absolute URL. */
  checkoutHref?: string
  note?: string
}

// ── Wishlist ("shopping list") ────────────────────────────────────────
//
// Things a visitor wants to come back to, kept in the browser like the
// cart (see `page-runtime/wishlist.ts`). Session-free, so it works on a
// published page; never a price authority — moving an item to the cart
// puts it in the basket, and the order is priced again server-side.

export interface WishlistButtonBlock extends BaseBlock {
  type: 'wishlist_button'
  /** Item code — one wishlist entry per code. Supports `{{vars}}`. */
  sku: string
  name: string
  /** As authored — "₹3,200"; split like an add-to-cart price. */
  price?: string
  image?: string
  /** Where the entry links back to. */
  href?: string
  /** A second line under the name. */
  meta?: string
  /** Label while the item is NOT saved. Default "Add to wishlist". */
  addLabel?: string
  /** Label while it IS saved. Default "Saved to wishlist". */
  removeLabel?: string
  /** `icon` = a round heart only (label becomes its accessible name);
   *  `button` = heart + label. Default 'button'. */
  display?: 'icon' | 'button'
  variant?: ButtonVariant
  /** Button size (the block's own `size` is its layout box). Default 'md'. */
  buttonSize?: 'sm' | 'md' | 'lg'
}

export interface WishlistBadgeBlock extends BaseBlock {
  type: 'wishlist_badge'
  label?: string
  /** Where the badge links. Default '/wishlist'. */
  href?: string
  /** Default 'heart'. */
  icon?: string
  /** Hide the count when the wishlist is empty. Default true. */
  hideWhenEmpty?: boolean
  variant?: ButtonVariant
  /** Icon with the count on its corner, no visible text — for a compact
   *  header. `label` becomes the link's accessible name. */
  iconOnly?: boolean
}

export interface WishlistLinesBlock extends BaseBlock {
  type: 'wishlist_lines'
  emptyText?: string
  continueLabel?: string
  continueHref?: string
  /** Default "Move to cart". */
  moveLabel?: string
  /** Default "Remove". */
  removeLabel?: string
  /** Offer "Move to cart" on each entry. Default true. */
  showMove?: boolean
  /** Keep the entry on the list after moving it to the cart. Default false. */
  keepAfterMove?: boolean
}

// ── Announcement / top bar (Tier 4.4) ──
// A slim, optionally dismissible strip ABOVE the site header. Site-level
// (part of the site manifest), not a block, because it must render on every
// page of the site regardless of which page is being viewed.
export interface AnnouncementBar {
  enabled?: boolean
  text: string
  href?: string
  linkLabel?: string
  dismissible?: boolean          // default true; the choice persists in localStorage
  background?: string
  textColor?: string
  /** Storage key for the dismissal; bump it to re-show an updated notice. */
  id?: string
}

// ── Variable display block ────────────────────────────────────
// Displays the live value of a variable on the page. Useful for showing
// API-fetched data or computed values in a more polished way than just
// a `{{name}}` token in a paragraph.

export type VariableDisplayFormat =
  | 'auto'                       // type-aware default
  | 'number'                     // 1,234.56
  | 'currency'                   // ₹1,234.56
  | 'percentage'                 // 12.3%
  | 'date'                       // local date
  | 'datetime'                   // local date + time
  | 'time'                       // local time
  | 'json'                       // stringified

export interface VariableBlock extends BaseBlock {
  type: 'variable'
  name: string                   // PageVariable.name to display
  label?: string                 // optional label rendered above the value
  format?: VariableDisplayFormat
  prefix?: string                // text before the value (e.g. "Total: ")
  suffix?: string                // text after the value (e.g. " kg")
  emptyText?: string             // shown when the variable is empty/null
  textSize?: 'sm' | 'md' | 'lg' | 'xl'
  align?: Align
  loadingText?: string           // shown while an API variable is loading
}

// ── Table block ───────────────────────────────────────────────
// A simple data table with column headers and row cells. For lists of
// records pulled from a transaction, prefer the `embed` block instead;
// `table` is for small static tables that live with the page content.

export type TableColumnAlign = 'left' | 'center' | 'right'

export interface TableColumn {
  id: string
  label: string
  align?: TableColumnAlign
  width?: string                 // CSS width: '120px' or '20%' etc.
}

export interface TableBlock extends BaseBlock {
  type: 'table'
  caption?: string
  heading?: string
  subheading?: string
  columns: TableColumn[]
  rows: string[][]               // each row is one string per column (markdown-ish line breaks ignored)
  striped?: boolean
  bordered?: boolean
  compact?: boolean
  align?: Align                  // wrapper alignment (left/center/right of the page)
}

// ── Notepad block ─────────────────────────────────────────────
// Rich-text "notepad": stores authored HTML content (subset of standard
// inline + block formatting tags). Renderers display the HTML directly;
// the editor uses a contentEditable surface with a formatting toolbar.
// `text` is an optional plain-text mirror used as a fallback when an
// older runtime cannot render HTML.

export interface NotepadBlock extends BaseBlock {
  type: 'notepad'
  html: string
  text?: string
  minHeight?: number             // px
}

// ── Rich content block ────────────────────────────────────────
// Renders the value of a RICHTEXT column — an article body, a product's long
// description, a department's "about" page — authored in a transaction form's
// rich-text editor. Unlike `notepad` (whose HTML is typed into the PAGE by its
// designer), the content here is DATA: one template page shows whichever
// record it is bound to. Every surface sanitises it with the same allow-list
// the engine applies on write (`@twasta/page-runtime/richContent`).

// Searches a content collection's published records in the browser. On the
// static site it reads the index the publisher writes beside the listing page
// (`indexUrl`, stamped at publish, e.g. '/blog/search.json'); in the in-app
// runtime it reads the same index from the API. Only what a card already
// shows is searchable (title, summary, terms).
export interface CollectionSearchBlock extends BaseBlock {
  type: 'collection_search'
  /** The content collection (transaction with a content profile). */
  collection: string
  placeholder?: string
  /** Most results shown at once. Default 12. */
  limit?: number
  emptyText?: string
  /** Set by the publisher — authors leave it empty. */
  indexUrl?: string
}

// Reader comments on one record of a collection: the APPROVED comments (name,
// date, text) and a form to add one. Comments live in the collection's
// comment transaction and are moderated in the Run view; the list is fetched
// live, so approving one needs no republish.
export interface CommentsBlock extends BaseBlock {
  type: 'comments'
  collection: string
  /** Which record — normally '{{item._slug}}' on the item template. */
  recordSlug: string
  heading?: string
  formHeading?: string
  emptyText?: string
  submitLabel?: string
}

export interface RichContentBlock extends BaseBlock {
  type: 'rich_content'
  /** The body — normally one binding to a RICHTEXT column: '{{item.body}}'
   *  on a collection template, '{{post.body}}' for a record data source. */
  source: string
  /** Table of contents built from the body's headings. 'auto' shows it only
   *  when the body has at least three. Default: off. */
  toc?: boolean | 'auto'
  tocTitle?: string
  /** 'article' = long-form reading typography; 'compact' = UI-dense. */
  typography?: 'article' | 'compact'
  /** Cap the line length for comfortable reading (~72 characters). */
  readableWidth?: boolean
  /** Open links to other sites in a new tab. */
  externalLinksNewTab?: boolean
  /** Mark links to other sites rel="nofollow" (user-generated content). */
  nofollowExternal?: boolean
  /** Shown when the bound value is empty. */
  emptyText?: string
}

export type FormFieldType =
  | 'text' | 'email' | 'tel' | 'textarea' | 'number'
  | 'select' | 'checkbox' | 'radio' | 'date' | 'file'
  | 'rating' | 'range' | 'consent' | 'url' | 'time' | 'hidden'

export interface FormFieldSpec {
  id: string
  name: string
  label: string
  type: FormFieldType
  placeholder?: string
  required?: boolean
  defaultValue?: string
  options?: PageInputOption[]
  visibility?: BlockVisibility
  validations?: ValidationRule[]
  helpText?: string
  /** rating: how many stars (default 5). range: bounds + step. */
  max?: number
  min?: number
  step?: number
  /** Multi-step forms: which step (1-based) this field belongs to. */
  step_no?: number
}

// ── Submission handling (Tier 8B) ────────────────────────────────────
// Authoring was always strong (validation, conditional fields, an action
// pipeline) but a form with no author-wired `call_api` step silently
// submitted nowhere. `destination` gives every form a real default.
//
//   'action'      — legacy: dispatch `submitAction` only (no-op when unset).
//   'submissions' — POST to the built-in submissions store; the rows show up
//                   in the workbench "Form submissions" inbox for this page.
//                   This is the DEFAULT for a form with no submitAction.
//   'email'       — store AND notify `notifyEmail`.
//   'transaction' — store AND save the submission as a RECORD of a
//                   transaction in this project (`record`), e.g. an Enquiries
//                   table staff work in the Run view. `notifyEmail`, when set,
//                   is mailed too.
//
// What a destination DOES is read by the server from the saved page model —
// the browser only sends the block id — so none of it can be steered by a
// visitor (see backend services/form_record.py).
export type FormDestination = 'action' | 'submissions' | 'email' | 'transaction'

// Where a `destination: 'transaction'` form saves its submission.
export interface FormRecordTarget {
  /** A transaction object of this project. */
  obj_name: string
  /** Form field name → column. A field left out saves to the column of the
   *  same name when one exists; map it to '-' to keep it out of the record. */
  fields?: Record<string, string>
  /** Fixed column values every record gets (e.g. `{ STATUS: 'NEW' }`). */
  set?: Record<string, string>
  /** Column that receives the slug of the collection record the page shows —
   *  e.g. the post a comment or enquiry was made on. Item pages only. */
  recordColumn?: string
}

// How much of the check a single form departs from the project default.
//   'auto'   — run reCAPTCHA whenever the visitor is NOT signed in. The
//              default, and the reason an author gets protection without
//              deciding to.
//   'off'    — never. For a form behind something that already proves a
//              person, or one whose false-positive cost is unacceptable.
//   'always' — even for a signed-in visitor.
export type FormCaptchaMode = 'auto' | 'off' | 'always'

// reCAPTCHA v3 returns a SCORE, not a verdict, so what a low one MEANS is a
// policy, not a fact.
//   'flag'   — store the submission and mark it in the inbox. Default,
//              because a threshold nobody has tuned must not eat enquiries.
//   'reject' — refuse it outright.
// A missing or forged token is not governed by this: that is a fact about the
// request, and it always flags (a form is never dropped outright).
export type FormCaptchaOnLowScore = 'flag' | 'reject'

// Anti-spam. `honeypot` adds a visually-hidden field real users never fill;
// a submission that fills it is silently accepted client-side and dropped
// server-side. `minSeconds` rejects submissions completed impossibly fast.
// The submissions endpoint additionally rate-limits per IP regardless.
//
// reCAPTCHA v3 sits on top of all three and is AUTOMATIC: any form a visitor
// can reach without signing in runs it, with no per-form authoring, as soon as
// the project (or the instance) has keys. The keys themselves are NOT here —
// they live in Advanced → Spam protection, because a v3 key is bound to a
// domain list and belongs to whoever administers the domain, not to one block
// on one page. These fields only say how this form departs from that default.
export interface FormSpamGuard {
  honeypot?: boolean             // default true
  honeypotName?: string          // default '_pg_hp'
  minSeconds?: number            // default 2
  turnstileSiteKey?: string      // Cloudflare Turnstile — declared, not yet wired
  /**
   * DEPRECATED. A per-form site key predates Advanced → Spam protection and is
   * ignored: a key pasted here has no secret to verify against, so the check
   * would run in the browser and never be checked. Configure the project.
   */
  recaptchaSiteKey?: string
  /** Default 'auto'. */
  captcha?: FormCaptchaMode
  /** Default: the project's setting (itself 'flag' unless changed). */
  captchaOnLowScore?: FormCaptchaOnLowScore
  /** 0.0-1.0. Default: the project's threshold (0.5 unless changed). */
  captchaMinScore?: number
}

// What the visitor sees after a successful submit. `order_submit` has had
// this trio since it shipped; `form`/`contact_form`/`newsletter` never did,
// so a successful submission left the page looking like nothing happened.
export interface FormSuccess {
  message?: string               // replaces the form with this confirmation
  title?: string
  redirectUrl?: string           // navigate here instead (wins over `message`)
  /** Keep the form on screen and clear it instead of replacing it. */
  keepForm?: boolean
}

export interface FormBlock extends BaseBlock {
  type: 'form'
  heading?: string
  subheading?: string
  fields: FormFieldSpec[]
  submitLabel: string
  submitAction?: string          // PageAction.name to invoke on submit
  layout?: 'stacked' | 'grid'
  align?: Align
  // ── Tier 8B ──
  destination?: FormDestination
  notifyEmail?: string           // destination 'email' (and optional for 'transaction')
  record?: FormRecordTarget      // destination 'transaction'
  spam?: FormSpamGuard
  success?: FormSuccess
  /** Render as a multi-step wizard using each field's `step_no`. */
  wizard?: boolean
  stepLabels?: string[]
}

// ── Saved-section preset (persisted under project's presets folder) ──
export interface SectionPreset {
  id: string                     // 'preset_<base36>'
  name: string
  description?: string
  category?: string
  thumbnail?: string             // data URL or asset URL
  section: Section               // template body — IDs are regenerated on insert
  createdAt: string
}

// ── Site / project-level navigation graph (P5) ───────────────────────
export interface MenuItem {
  id: string
  label: string
  href?: string                  // external URL
  routeId?: string               // resolved against project routes
  children?: MenuItem[]
}
export interface Menu {
  id: string
  name: string
  items: MenuItem[]
}
export interface SiteRoute {
  id: string
  path: string                   // '/about', '/products/:id'
  pageObjName: string            // target smart-page obj_name
  title?: string
  isHome?: boolean
  /**
   * Collection binding (Tier 8D). When present, this route is a TEMPLATE:
   * the publisher expands it into one static page per row of the bound data
   * source, substituting the row into `:param` segments of `path`. Without
   * it a ':id' segment was documented but never generated, so a blog /
   * case-study / product-detail story had no way to publish.
   */
  collection?: RouteCollection
}

export interface RouteCollection {
  /** PageDataSource.id (mode:'list') on the template page. */
  source: string
  /**
   * Row column supplying each generated page's URL segment. Its value is
   * slugified, so 'Acme Corp' → 'acme-corp'.
   */
  slugColumn: string
  /** Row column used as the generated page's <title> / og:title. */
  titleColumn?: string
  /** Row column used as the meta description. */
  descriptionColumn?: string
  /** Row column holding the social image URL. */
  imageColumn?: string
  /** Cap on generated pages (safety valve). Default 500. */
  limit?: number
  /**
   * Alias the row is published under inside the template page, so bindings
   * read `{{item.title}}`. Default 'item'.
   */
  as?: string
}

/** Pagination for a repeated list on a collection INDEX page. */
export interface ListPagination {
  enabled?: boolean
  pageSize?: number              // default 12
  /** 'pages' emits numbered static pages (/blog/2/); 'more' is a JS button. */
  style?: 'pages' | 'more'
  prevLabel?: string
  nextLabel?: string
}

export interface SiteGraph {
  routes: SiteRoute[]
  menus: Menu[]
  defaultLocale?: string
  locales?: string[]
  settings?: SiteSettings
}

// ── Storefront commerce blocks (checkout, order history, reviews) ─────
//
// Three blocks that each own a SERVER door, unlike the cart blocks above
// (which are browser-only). Every one of them is configured with COLUMN
// NAMES, never values the browser is trusted with: the server reads the
// block from the SAVED page (`form_record.find_page_block`) and takes the
// object, the columns, the fixed values and the approval values from there.
// See docs/plans/storefront-commerce-blocks-design.md.

/** Totals a checkout summary DISPLAYS (same shape as the cart_summary's
 *  rules). Display only — the order is priced by the application's own
 *  business logic and the confirmation shows the total it saved. */
export interface CartTotalsRules {
  taxPercent?: number
  taxLabel?: string
  shippingFlat?: number
  shippingFreeOver?: number
  shippingLabel?: string
  freeLabel?: string
}

/** One field the shopper fills on a `cart_checkout`. */
export interface CheckoutField {
  /** Field key — what the browser sends; unique within the block. */
  key: string
  label: string
  /** Order HEADER column the value is saved into. Omit for a field that only
   *  feeds `composeAddress` and/or the saved address. */
  column?: string
  /** Column on the saved-address object (`addresses.object`). */
  addressColumn?: string
  kind?: 'text' | 'email' | 'tel' | 'textarea' | 'postcode'
  required?: boolean
  /** HTML autocomplete token ('name', 'email', 'address-line1', …). */
  autocomplete?: string
  /** Pre-filled from the signed-in shopper's identity. */
  prefill?: 'name' | 'email' | 'phone'
  group?: 'contact' | 'address'
  /** The server caps every value at 400 characters regardless. */
  maxLength?: number
}

/** A discount / referral code the shopper may enter on a `cart_checkout`
 *  (a creator's code, a coupon). The browser sends the CODE only: the server
 *  looks it up in `lookup.object` as the owner (never an object kept per
 *  account) and returns the percent and who shared it — mapped columns only.
 *  The code is saved into `column` on the order and the order's own business
 *  logic prices it; an unknown code is refused when the order is placed. */
export interface CheckoutPromo {
  /** Order HEADER column the code is saved into. */
  column: string
  lookup: {
    object: string
    codeColumn: string
    /** Percent off (0–100], applied per line before tax in the estimate. */
    percentColumn: string
    /** Who shared the code (a creator handle, a partner name) — `{{label}}`. */
    labelColumn?: string
    /** Active flag column and the value meaning active (default 'Y'). */
    activeColumn?: string
    activeValue?: string
    validFromColumn?: string
    validToColumn?: string
    /** Minimum order subtotal for the code. */
    minOrderColumn?: string
  }
  /** Field label. Default 'Discount code'. */
  label?: string
  placeholder?: string
  /** Confirmation once applied; tokens `{{code}}`, `{{label}}`, `{{percent}}`.
   *  Default '{{code}} applied'; a text naming `{{label}}` falls back to that
   *  default for a code with no label. Shown in the summary, so the box needs
   *  `showSummary` (the default). */
  appliedText?: string
  /** Summary line; tokens as above. Default 'Discount ({{percent}}%)'. */
  discountLabel?: string
}

export interface CartCheckoutBlock extends BaseBlock {
  type: 'cart_checkout'
  order: {
    /** The order transaction (a header with a lines child form). */
    object: string
    /** Which child form the cart's lines become, and which of its columns
     *  take the sku and quantity (+ booking columns). REUSED from a page
     *  input's `cart` source. Only sku/qty/dates travel — the lines are
     *  priced by the order's own item_change / business logic. */
    lines: PageCartSource
    /** Fixed header values, applied by the SERVER (column → value). */
    set?: Record<string, string>
    /** One header column composed from several fields:
     *  `'{{line1}}, {{city}} {{pin}}'` (tokens are field keys). */
    composeAddress?: { column: string; template: string }
    /** Header column read back after saving for the confirmation (order no). */
    referenceColumn?: string
    /** Header column read back after saving for the confirmation (total). */
    totalColumn?: string
  }
  fields: CheckoutField[]
  /** Saved addresses of a signed-in shopper. The object MUST keep a record per
   *  account (row security sourced from the user id). */
  addresses?: { object: string; labelColumn?: string; allowSave?: boolean }
  /** Page variable receiving the saved order's txn id — what a
   *  `payment_checkout` reads through its `txnIdVar`. */
  orderIdVar: string
  /** Ask for sign-in before checkout even when the order object would accept
   *  a guest. (An order object kept per account always requires it.) */
  requireSignIn?: boolean
  /** Empty the basket once the order is saved. Default 'on_order'. */
  clearCart?: 'on_order' | 'never'
  showSummary?: boolean
  summaryRules?: CartTotalsRules
  /** Discount / creator code box in the order summary. */
  promo?: CheckoutPromo
  /** Site link opened after the order is saved; `{{id}}` / `{{reference}}`. */
  onSuccessNavigateTo?: string
  heading?: string
  contactHeading?: string
  addressHeading?: string
  submitLabel?: string
  emptyText?: string
  emptyCtaHref?: string
  successTitle?: string
  successBody?: string
  /** Put the submit button at the foot of the order summary card instead of
   *  under the forms. Default false. */
  submitInSummary?: boolean
  /** A highlighted note under the forms (e.g. "Followed a creator? Enter
   *  their code in the order summary"). */
  note?: { title?: string; body?: string; icon?: string }
  /** A signed-in shopper whose contact fields are all filled from the account
   *  sees `card` — "Signed in as …" with a Change link that opens the fields —
   *  instead of the fields themselves. Default 'fields'. */
  signedInContact?: 'fields' | 'card'
  /** Order summary wording. Defaults: "Order summary", "Estimated total",
   *  "Prices, tax and delivery are confirmed when the order is placed." */
  summaryHeading?: string
  summaryTotalLabel?: string
  summaryNote?: string
}

/** One field of a `saved_records` card's add/edit dialog. */
export interface SavedRecordsField {
  /** Stable key the browser uses; the server maps it to `column`. */
  key: string
  label: string
  /** Column of the block's object. Read from the SAVED page, never the request. */
  column: string
  kind?: 'text' | 'tel' | 'email' | 'postcode' | 'select' | 'textarea'
  required?: boolean
  placeholder?: string
  autocomplete?: string
  /** Fixed choices for `kind: 'select'`. */
  options?: { value: string; label: string }[]
  /** Choices resolved by the SERVER: `payment_methods` = what the project's
   *  enabled payment gateway offers (UPI / card / net banking / wallet) plus
   *  Cash on Delivery when it is on — so a saved preference can never name a
   *  method checkout will not accept. */
  optionsFrom?: 'payment_methods'
  /** Only shown (and only saved) while another field holds one of `in`. */
  showWhen?: { key: string; in: string[] }
  /** Where the value appears on the card: its title, a body line, or a small
   *  muted line. Fields with no role are edited but not shown. */
  role?: 'title' | 'line' | 'meta' | 'status'
  /** Pre-fill a NEW entry from the signed-in account. */
  prefill?: 'name' | 'email' | 'phone'
  /** `false` = shown on the card only, never in the form and never written
   *  from the browser (a status the team sets, an id the system assigns). */
  inForm?: boolean
  /** Section heading the field starts in the form (e.g. "About you"). */
  group?: string
  /** Max characters (also enforced by the column's own size). */
  maxLength?: number
  /** Exact-length digits, e.g. the last 4 of a card. */
  pattern?: string
  /** On the card, continue the previous `line` with ", " instead of a new
   *  line (city, state, PIN on one line). */
  joinPrevious?: boolean
  /** Dialog layout: two narrow fields share a row on wide screens. */
  half?: boolean
}

/**
 * The signed-in visitor's OWN saved records — an address book, saved payment
 * preferences — as cards with Add / Edit / Delete / Make default. Live-only.
 * The object must be kept per account (row security on the visitor's id);
 * the server lists, saves and deletes AS the visitor, so the object's own
 * rules (validations, business logic such as "one default") apply.
 */
export interface SavedRecordsBlock extends BaseBlock {
  type: 'saved_records'
  object: string
  /** Picks the card icon and the default wording. */
  kind?: 'address' | 'payment_method' | 'generic'
  fields: SavedRecordsField[]
  /** Column holding the default flag, with its yes / no values. */
  defaultColumn?: string
  defaultValue?: string
  notDefaultValue?: string
  heading?: string
  addLabel?: string
  emptyText?: string
  /** Title of the add / edit dialog. */
  formTitle?: string
  /** Refuse adding beyond this many. */
  maxRecords?: number
  /** One entry per account (an application, a profile): before it exists the
   *  form shows inline; afterwards the entry shows as a card. */
  single?: boolean
  /** Offer Delete on a card. Default true. */
  allowDelete?: boolean
  /** Edit only while this field holds one of `in` (e.g. an application is
   *  still pending). Enforced on the server. */
  editableWhen?: { key: string; in: string[] }
  /** Words / tones for a `role: 'status'` field's stored values. */
  statusLabels?: Record<string, string>
  statusTones?: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'>
  /** Submit button wording. Default "Save". */
  submitLabel?: string
  /** Line under the form heading. */
  intro?: string
  /** Shown once after a successful first save. */
  successText?: string
  /** `single` only: before the entry exists, show this button (with
   *  `startText` above it) and open the form when it is pressed — "Apply",
   *  rather than a form the visitor did not ask for. Absent = form inline. */
  startLabel?: string
  startText?: string
  /** A sentence on the card per stored `role: 'status'` value — what that
   *  state MEANS for the visitor ("Your request is in review…"). */
  statusNotes?: Record<string, string>
}

export interface MyRecordsBlock extends BaseBlock {
  type: 'my_records'
  /** The order transaction. MUST keep a record per account. */
  object: string
  columns: {
    reference: string
    date: string
    status?: string
    paymentStatus?: string
    total?: string
    /** The money, shown as a breakdown under the lines (all optional). */
    subtotal?: string
    discount?: string
    discountCode?: string
    tax?: string
    shipping?: string
    paidAmount?: string
    paymentMethod?: string
    /** Delivery address, shown on the single-order view. */
    address?: string
  }
  /** Show ONE of the caller's orders, its id from this URL parameter (an
   *  order-confirmation page). Someone else's id is simply not found. */
  recordFromQuery?: string
  /** Heading / line shown above a single order (confirmation wording). */
  singleTitle?: string
  singleBody?: string
  /** "Complete payment" on an order whose payment status is one of `when`
   *  (blank counts as ''); `href` takes `{{id}}` / `{{reference}}`. */
  pay?: { label?: string; href: string; when: string[]; hideWhenStatus?: string[] }
  /** Words for stored payment-method / status values. */
  valueLabels?: Record<string, string>
  /** "Continue shopping" link under the list / single order. */
  continueHref?: string
  continueLabel?: string
  /** Status value → chip tone. */
  statusTones?: Record<string, 'success' | 'warning' | 'error' | 'info' | 'neutral'>
  currencyPrefix?: string
  lines?: {
    detailFormNo: string
    sku: string
    name: string
    qty: string
    unitPrice?: string
    amount?: string
    image?: string
    /** Picture looked up from another object by sku. */
    imageFrom?: { object: string; keyColumn: string; imageColumn: string }
    /** Product link; `{{sku}}`. */
    hrefTemplate?: string
  }
  /** "Buy again": the order's lines go back into the cart. */
  reorder?: { label?: string; cartHref?: string } | false
  /** Invoice link; `{{id}}` / `{{reference}}`. */
  invoiceHref?: string
  /** Default 10, max 25. */
  pageSize?: number
  heading?: string
  emptyText?: string
  emptyCtaLabel?: string
  emptyCtaHref?: string
}

export interface ReviewsBlock extends BaseBlock {
  type: 'reviews'
  /** The review transaction. Must NOT be kept per account — reviews are
   *  public once approved. */
  object: string
  /** The record reviewed: a literal or a token, `'{{product.PRODUCT_CODE}}'`. */
  record: string
  columns: {
    record: string
    rating: string
    body: string
    status: string
    title?: string
    authorName?: string
    authorId?: string
    date?: string
    /** Review column stamped with `verifiedValue` when the writer passed
     *  `purchaseCheck`; the list shows a "Verified purchase" badge. */
    verified?: string
  }
  /** Value written into `columns.verified`. Default 'Y'. */
  verifiedValue?: string
  /** Only a customer who BOUGHT the record may review it. Checked by the
   *  server, listed AS the signed-in shopper on `object` (which must keep its
   *  records per account): at least one of their records — or, with
   *  `detailFormNo`, a line of one — has `recordColumn` = the reviewed record,
   *  optionally only where the HEADER's `statusColumn` is one of
   *  `statusValues` (delivered / paid). Implies sign-in to write. */
  purchaseCheck?: {
    object: string
    detailFormNo?: string
    recordColumn: string
    statusColumn?: string
    statusValues?: string[]
  }
  /** Status value a review must hold to be listed. */
  approvedValue: string
  /** Status value a new review is saved with (server-set). */
  pendingValue: string
  /** Default true — a new review waits for approval. */
  moderate?: boolean
  /** Refuse a review of a record that does not exist. */
  recordCheck?: { object: string; column: string }
  /** Who may write one. Default 'signed_in'. */
  submit?: 'signed_in' | 'anyone' | 'off'
  /** One review per account per record. Default true. */
  onePerAccount?: boolean
  pageSize?: number
  heading?: string
  formHeading?: string
  emptyText?: string
  submitLabel?: string
  showSummary?: boolean
}

/** Blocks that cannot be prerendered into a static page — they need the
 *  live application (a session, the engine). A page holding one is served
 *  by the live site (SPA) instead of the static bundle. `auth_form` and
 *  `input_group` have no static renderer at all: baked, a sign-in page
 *  published as a heading with no fields. */
export const LIVE_ONLY_BLOCK_TYPES = ['cart_checkout', 'my_records', 'saved_records', 'auth_form', 'input_group'] as const

/** Page actions that change records or re-read data — they need the engine,
 *  so a page using one is a live screen, not a static document. */
const LIVE_ACTION_STEPS = ['txn_action', 'txn_update', 'refresh_source']

function stepsNeedLive(steps: unknown): boolean {
  if (!Array.isArray(steps)) return false
  return steps.some((st) => {
    if (!st || typeof st !== 'object') return false
    const s = st as { kind?: string; on_success?: unknown; on_error?: unknown }
    return LIVE_ACTION_STEPS.includes(String(s.kind)) || stepsNeedLive(s.on_success) || stepsNeedLive(s.on_error)
  })
}

/** Does this page hold a block that only the live runtime can serve?
 *  Walks sections, nested container/columns/tabs slots and legacy blocks.
 *  A LIVE OPERATIONAL SCREEN counts too — a polled data source, a selectable
 *  repeat, a record-changing action, terminal mode: baked, it would publish
 *  as a frozen snapshot whose taps do nothing. */
export function pageNeedsLiveRuntime(page: PageModel | null | undefined): boolean {
  if (!page) return false
  if ((page.dataSources || []).some((d) => d && d.refresh && d.bindingTime === 'runtime')) return true
  if ((page.actions || []).some((a) => a && stepsNeedLive(a.steps))) return true
  if (page.chrome?.terminal) return true
  const live = LIVE_ONLY_BLOCK_TYPES as readonly string[]
  const walk = (blocks: unknown): boolean => {
    if (!Array.isArray(blocks)) return false
    for (const b of blocks) {
      if (!b || typeof b !== 'object') continue
      const blk = b as Record<string, unknown>
      if (typeof blk.type === 'string' && live.includes(blk.type)) return true
      if ((blk.repeat as { select?: unknown } | undefined)?.select) return true
      // An embedded transaction / process / dashboard / visual / report has no
      // static rendering — the prerender drops it and the page published with
      // its form missing (a partner application page with no application
      // form). Only an embedded SMART PAGE inlines statically.
      if (blk.type === 'embed') {
        const et = (blk.embed as { obj_type?: string } | undefined)?.obj_type
        if (et && et !== 'S') return true
      }
      if (walk(blk.children) || walk(blk.blocks)) return true
      for (const key of ['columns', 'items', 'tabs', 'slides']) {
        const slot = blk[key]
        if (Array.isArray(slot)) {
          for (const s of slot) {
            if (s && typeof s === 'object' && walk((s as Record<string, unknown>).blocks)) return true
          }
        }
      }
    }
    return false
  }
  if (Array.isArray(page.sections)) {
    for (const s of page.sections) if (s && walk(s.blocks)) return true
  }
  return walk(page.blocks)
}
